// Investor Symposium — Team Console (spec §6; mockup
// /mnt/projects/WISE/REMS/wpi-symposium-console-mockup.html is the visual
// reference). Six tabs: Overview · Invitation List · RSVP Tracking ·
// Agenda Builder · Playbook · Recurring Setup.
//
// Outbound email is LIVE in prod (Graph as events@, 2026-07-30) behind the
// SYMPOSIUM_EMAIL_ENABLED kill switch; where the flag is off the API still
// refuses sends (409) and the wave/email modals banner it. Wave DELIVERY is
// a later slice (501). Sending, per-invite Preview/Email, and the Agenda and
// Playbook tabs gate on crm.events.manage — i.e. Client Relations, who own
// the event (narrowed from crm.manage 2026-09-09; the old line left the
// owning team unable to email anyone). Event-level email copy, the investor
// landing page and Recurring Setup stay crm.manage.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarRange, Pencil, Plus, Send, Download, ShieldAlert, ExternalLink, Monitor, Smartphone, Search, UserPlus, Users, GitMerge } from 'lucide-react'
import {
  PageHeader, SectionCard, Badge, Button, EmptyState, Tabs, DataTable,
  KpiGrid, KpiTile, Modal, Field, TextInput, TextArea, Select, ListToolbar,
  type Column, type TabDef, type BadgeTone,
} from '@/components/ui'
import { useStore, useHasPermission } from '@/store/useStore'
import { useApiToken } from '@/auth/useApiToken'
import { api, API_BASE } from '@/api/client'
import { contactName, coupleName } from '@/lib/selectors'
import { date as fmtDate, num } from '@/lib/format'
import type { Account, Address, Contact } from '@/types/entities'
import { downloadCsv } from '@/lib/csv'
import { DeliveryTab } from './DeliveryTab'
import { HeadcountCard, type HeadcountKpis } from './HeadcountCard'
import { GIFT_DELIVERY_OPTIONS, giftDeliveryLabel, giftDeliveryRank, type GiftDeliveryValue } from './gift-delivery'
// Second door on the same screen (Chauncey, 2026-09-10): the duplicates come
// from the symposium import, so the people working this list are the ones who
// spot them. The Contacts-list entry point stays where it is.
import { MergeDuplicatesModal } from '@/pages/contacts/MergeDuplicatesModal'
import './SymposiumConsole.css'

type TabId = 'overview' | 'invites' | 'rsvp' | 'delivery' | 'agenda' | 'playbook' | 'portal' | 'email' | 'recurring'

const TABS: TabDef[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'invites', label: 'Invitation List' },
  { id: 'rsvp', label: 'RSVP Tracking' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'agenda', label: 'Agenda Builder' },
  { id: 'playbook', label: 'Playbook' },
  { id: 'portal', label: 'Portal Preview' },
  { id: 'email', label: 'Email Preview' },
  { id: 'recurring', label: 'Recurring Setup' },
]

// Admin-only tabs. Agenda + Playbook moved out 2026-09-09 (Chauncey): the
// role description already gave Client Relations "agenda, playbook", and
// they run the event. What remains is what every INVITEE sees or what
// configures future events — the email copy, the investor landing page, and
// recurring setup. The API enforces the same line server-side (requireAdmin).
const ADMIN_TAB_IDS = new Set<string>(['portal', 'email', 'recurring'])

const RSVP_TONE: Record<string, BadgeTone> = { yes: 'ok', maybe: 'warn', no: 'alert', none: 'neutral' }

// Manual RSVP (Chauncey 2026-09-03): investors who could not use the link
// answer by replying to events@ or by phone, and Client Relations records
// it here. Mirrors the portal's four buttons. The API stamps rsvp_at,
// rsvp_source='manual' and rsvp_by, so a hand-entered answer is never read
// as proof that the link worked (the Delivery tab says "entered by hand").
const RSVP_CHOICES: Array<{ value: string; label: string; rsvp: string; partySize: number }> = [
  { value: 'none', label: '—', rsvp: 'none', partySize: 1 },
  { value: 'yes', label: 'Yes', rsvp: 'yes', partySize: 1 },
  { value: 'yes2', label: 'Yes +1', rsvp: 'yes', partySize: 2 },
  { value: 'maybe', label: 'Maybe', rsvp: 'maybe', partySize: 1 },
  { value: 'no', label: 'No', rsvp: 'no', partySize: 1 },
]
function rsvpChoiceValue(i: { rsvp?: string | null; partySize?: number | null }): string {
  if (i.rsvp === 'yes') return (i.partySize ?? 1) >= 2 ? 'yes2' : 'yes'
  return i.rsvp && i.rsvp !== 'none' ? i.rsvp : 'none'
}
function RsvpSelect({ invite, onPatch }: { invite: any; onPatch: (body: { rsvp: string; partySize: number }) => void }) {
  const answered = !!invite.rsvp && invite.rsvp !== 'none'
  const manual = invite.rsvpSource === 'manual'
  const when = invite.rsvpAt ? ` · ${new Date(invite.rsvpAt).toLocaleString()}` : ''
  const title = answered
    ? (manual ? `Entered by ${invite.rsvpBy ?? 'staff'}${when}`
      : invite.rsvpSource === 'portal' ? `Answered on the portal${when}`
        : invite.rsvpSource === 'self_register'
          ? `Registered at /symposium/rsvp, approved by ${invite.invitedBy ?? 'the team'}${when}`
          : `Answered${when}`)
    : 'No answer yet — pick one to record an RSVP received by email or phone'
  return (
    <span className={`sym-rsvp sym-rsvp--${invite.rsvp ?? 'none'}`} title={title}>
      <select
        className="sym-inline-select"
        value={rsvpChoiceValue(invite)}
        onChange={(e) => {
          const c = RSVP_CHOICES.find((x) => x.value === e.target.value)
          if (c) onPatch({ rsvp: c.rsvp, partySize: c.partySize })
        }}
      >
        {RSVP_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
      {answered && manual && <span className="sym-rsvp-manual" aria-label="entered by hand">by hand</span>}
    </span>
  )
}

// The annual investor gift (Chauncey + Donna, 2026-09-29; migration 030).
// Gift Yes/No is a DECISION and defaults to No — the old column read Yes for
// everyone nobody had looked at. Only a Yes has a delivery: collected or
// ordered at the Symposium, or mailed (not everyone is invited or can come,
// and they still get one). Same two controls on the Invitation List and the
// RSVP roster, where "Picked up at Symposium" is the check-in handover.
function GiftSelect({ invite, onPatch }: { invite: any; onPatch: (body: { gift: boolean }) => void }) {
  return (
    <select
      className="sym-inline-select"
      value={invite.gift ? 'yes' : 'no'}
      title="Does this investor get the annual gift? Shirt sizes live on the contact profile."
      onChange={(e) => {
        const gift = e.target.value === 'yes'
        // Saying No clears a recorded delivery (the API does it in the same
        // write), so a stray click cannot silently erase "Mailed".
        if (!gift && invite.giftDelivery
          && !confirm(`This gift is recorded as “${giftDeliveryLabel(invite.giftDelivery)}”. Setting Gift to No clears that. Continue?`)) return
        onPatch({ gift })
      }}
    >
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  )
}

function GiftDeliverySelect({ invite, onPatch }: {
  invite: any; onPatch: (body: { giftDelivery: GiftDeliveryValue | null }) => void
}) {
  const on = !!invite.gift
  const when = invite.giftDeliveryAt ? ` · recorded ${new Date(invite.giftDeliveryAt).toLocaleString()}` : ''
  const hint = !on
    ? 'Gift is No — set Gift to Yes to record how it reaches them'
    : invite.giftDelivery ? `${giftDeliveryLabel(invite.giftDelivery)}${when}` : 'Not yet picked up or mailed'
  // The hint rides on a wrapper: a disabled <select> shows no tooltip.
  return (
    <span title={hint}>
      <select
        className="sym-inline-select"
        value={on ? invite.giftDelivery ?? '' : ''}
        disabled={!on}
        aria-label="Gift delivery"
        onChange={(e) => onPatch({ giftDelivery: (e.target.value || null) as GiftDeliveryValue | null })}
      >
        <option value="">—</option>
        {GIFT_DELIVERY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </span>
  )
}

/** Gift Yes/No with its delivery directly beneath, in ONE cell. Both tables
 *  were already full: as two columns, the new delivery select sat behind a
 *  horizontal scroll below ~1680px, and on the roster it pushed "Check in"
 *  off-screen at 1440px — the one button the event desk cannot lose. */
function GiftCell({ invite, onPatch }: { invite: any; onPatch: (body: object) => void }) {
  return (
    <span className="sym-gift-cell">
      <GiftSelect invite={invite} onPatch={onPatch} />
      <GiftDeliverySelect invite={invite} onPatch={onPatch} />
    </span>
  )
}

/** Sort key for the gift cell: Yes rows first, in delivery order; No last. */
const giftSortValue = (i: any): number => (i.gift ? giftDeliveryRank(i.giftDelivery) : 99)

/** Where a gift is posted: the contact's own office address; when the card says
 *  "Office same as account HQ" and holds none of its own, the account's HQ —
 *  the same fallback the contact page shows. */
function mailingAddress(c: Contact | undefined, accountById: Map<string, Account>): Address | undefined {
  if (!c) return undefined
  const own = c.officeAddress
  if (own && (own.line1 || own.line2 || own.city || own.state || own.zip)) return own
  if (c.sameAsHQ && c.accountId) return accountById.get(c.accountId)?.hqAddress ?? own
  return own
}

const STATUS_TONE: Record<string, BadgeTone> = {
  invited: 'ok', pending_review: 'neutral', waitlist: 'warn', not_invited: 'alert',
}
const STATUS_LABEL: Record<string, string> = {
  invited: 'Invited', pending_review: 'Pending review', waitlist: 'Waitlist', not_invited: 'Not invited',
}
// data_flag = the import's cleanup queue (e.g. rows from Donna's workbook
// that landed without an email address). Humanize; unknown values pass raw.
const FLAG_LABEL: Record<string, string> = { missing_email: 'Missing email', bounced_email: 'Email bounced', invalid_email: 'Invalid email' }

// The email templates (services/symposium-email-content.ts owns the copy
// defaults; the console only picks + edits). Same order as the API's
// EMAIL_KINDS. The invitation and the keynote update share Jason's keynote
// artwork; the keynote update is for guests who already said yes.
const EMAIL_KINDS = [
  { id: 'save_the_date', label: 'Save the Date' },
  { id: 'invitation', label: 'Invitation' },
  { id: 'reminder', label: 'Reminder' },
  { id: 'final_call', label: 'Final call' },
  { id: 'keynote_update', label: 'Keynote update (RSVP’d yes)' },
] as const
type EmailKindId = (typeof EMAIL_KINDS)[number]['id']

interface EventBundle {
  event: any
  agenda: any[]
  waves: any[]
  playbook: any | null
  kpis: HeadcountKpis & {
    masterList: number; invited: number
    declined: number; pending: number; dataFlags: number; clicked: number; visited: number
    /** Written only by the send path — never by a human editing a dropdown.
     *  `emailed` excludes the `needsResend` rows: they were mailed, but at an
     *  address corrected since, so nothing reached the person. */
    emailed: number; invitedNotEmailed: number; needsResend: number
    /** WPI employees invited to ATTEND — counted apart from the investor
     *  funnel so 200 colleagues cannot flatter the conversion numbers. */
    staffGuests: number; staffGuestsEmailed: number; staffRsvpYes: number
  }
  emailEnabled: boolean
}

// Invitation List filters live on the console (not the tab) so the Overview
// KPI tiles can drill into a pre-filtered list.
interface InviteFilters { search: string; tier: number | null; status: string; rsvp: string; gift: string; flag: string; history: string; sent: string }
const EMPTY_FILTERS: InviteFilters = { search: '', tier: null, status: '', rsvp: '', gift: '', flag: '', history: '', sent: '' }

/** history filter values: 'att:2025' / 'att:any' / 'att:never' / 'inv:2024' / 'inv:never' */
function matchesHistory(value: string, c?: Contact): boolean {
  const attended = c?.symposiumYears ?? []
  const invited = c?.symposiumInvitedYears ?? []
  const [kind, arg] = value.split(':')
  if (kind === 'att') {
    if (arg === 'any') return attended.length > 0
    if (arg === 'never') return attended.length === 0
    return attended.includes(Number(arg))
  }
  if (kind === 'inv') {
    if (arg === 'never') return attended.length === 0 && invited.length === 0
    return invited.includes(Number(arg))
  }
  return true
}

/** Company shown for an invite: the contact's LIVE account link wins; the
 *  invite row's `investorGroup` import-time snapshot is only a fallback.
 *  (Jason 7/28: Steve Cobbs moved Invesco → North Palisade in Contacts but
 *  the invite list kept showing the frozen "Invesco" string.) */
function companyFor(contactById: Map<string, Contact>, accountById: Map<string, Account>, i: any): string | null {
  const c = contactById.get(i.contactId)
  const acct = c?.accountId ? accountById.get(c.accountId) : undefined
  return acct?.name ?? i.investorGroup ?? null
}

function applyFilters(invites: any[], f: InviteFilters, contactById: Map<string, Contact>, accountById: Map<string, Account>): any[] {
  const q = f.search.trim().toLowerCase()
  return invites.filter((i) => {
    if (f.tier != null && i.tier !== f.tier) return false
    if (f.status && i.inviteStatus !== f.status) return false
    if (f.rsvp && i.rsvp !== f.rsvp) return false
    // Gift (migration 030): `gift` is the decision, default No. "Needs
    // mailing" is the API's rule (`giftNeedsMailing`), not re-derived here.
    if (f.gift === 'yes' && !i.gift) return false
    if (f.gift === 'no' && i.gift) return false
    if (f.gift === 'needs_mailing' && !i.giftNeedsMailing) return false
    if (f.gift === 'picked_up' && !(i.gift && i.giftDelivery === ('picked_up' satisfies GiftDeliveryValue))) return false
    if (f.gift === 'mailed' && !(i.gift && i.giftDelivery === ('mailed' satisfies GiftDeliveryValue))) return false
    if (f.flag === 'flagged' && !i.dataFlag) return false
    if (f.flag === 'clean' && i.dataFlag) return false
    // 'not_sent' = the honest question "who thinks they were invited but has
    // never been emailed?" — `emailSentAt` is written only by the send path.
    if (f.sent === 'sent' && (!i.emailSentAt || i.addressChanged)) return false
    if (f.sent === 'not_sent' && i.emailSentAt) return false
    // Mailed, but the address was corrected afterwards — the work queue for
    // "we fixed their email, who still needs a copy?" (2026-09-09).
    if (f.sent === 'needs_resend' && !i.addressChanged) return false
    if (f.history && !matchesHistory(f.history, contactById.get(i.contactId))) return false
    if (q) {
      const c = contactById.get(i.contactId)
      const hay = `${c ? contactName(c) : i.contactId} ${companyFor(contactById, accountById, i) ?? ''} ${c?.workEmail ?? ''} ${c?.personalEmail ?? ''}`.toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })
}

export default function SymposiumConsole() {
  const canManage = useHasPermission('crm.events.manage')
  const isAdmin = useHasPermission('crm.manage')
  const getToken = useApiToken()
  const contacts = useStore((s) => s.data.contacts)
  const accounts = useStore((s) => s.data.accounts)
  const [tab, setTab] = useState<TabId>('overview')
  const [bundle, setBundle] = useState<EventBundle | null>(null)
  const [invites, setInvites] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [inviteFilters, setInviteFilters] = useState<InviteFilters>(EMPTY_FILTERS)
  // events@ inbox status (prod only): the bounce scanner files NDRs, so the
  // inbox count ≈ human mail awaiting a human. Card hides when unavailable.
  const [mailbox, setMailbox] = useState<Awaited<ReturnType<typeof api.symposium.mailboxStatus>> | null>(null)
  // Delivery tab's wave picker lives here so the Overview timeline can deep-link
  // ("Delivery report" on a wave row). 'all' = every send on the event.
  const [deliveryWaveId, setDeliveryWaveId] = useState<string>('all')

  const contactById = useMemo(() => new Map(contacts.map((c) => [c.id, c])), [contacts])
  const accountById = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts])

  // Overview tiles drill here: land on the Invitation List pre-filtered.
  const drillToInvites = useCallback((f: Partial<InviteFilters>) => {
    setInviteFilters({ ...EMPTY_FILTERS, ...f })
    setTab('invites')
  }, [])
  const openDelivery = useCallback((waveId: string) => {
    setDeliveryWaveId(waveId)
    setTab('delivery')
  }, [])

  // WPI staff work the event — they are not invitees. They live in their own
  // area on the Invitation List tab and are excluded from every list, count,
  // wave audience, and the RSVP funnel/roster (the API excludes them from
  // the bundle KPIs the same way).
  // Archived contacts (admin-only; stripped server-side for non-managers)
  // drop off the invite lists entirely — unarchive to bring them back.
  const investorInvites = useMemo(
    () => invites.filter((i) => {
      const c = contactById.get(i.contactId)
      return c && !c.isWpi && !c.archived
    }),
    [invites, contactById],
  )
  const staffInvites = useMemo(
    () => invites.filter((i) => {
      const c = contactById.get(i.contactId)
      return c?.isWpi && !c.archived
    }),
    [invites, contactById],
  )

  const load = useCallback(async () => {
    try {
      setError(null)
      const token = await getToken()
      const events = await api.symposium.listEvents(token)
      if (!events.length) { setBundle(null); setInvites([]); setLoading(false); return }
      const current = events[0]
      const [b, inv] = await Promise.all([
        api.symposium.getEvent(token, current.id),
        api.symposium.listInvites(token, current.id),
      ])
      setBundle(b)
      setInvites(inv)
      setLoading(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    void (async () => {
      try { setMailbox(await api.symposium.mailboxStatus(await getToken())) } catch { /* badge just stays hidden */ }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Non-admins can land on an admin tab via stale state — snap back.
  useEffect(() => {
    if (!isAdmin && ADMIN_TAB_IDS.has(tab)) setTab('overview')
  }, [isAdmin, tab])
  const visibleTabs = useMemo(() => (isAdmin ? TABS : TABS.filter((t) => !ADMIN_TAB_IDS.has(t.id))), [isAdmin])

  if (!canManage) {
    return (
      <div className="sym">
        <PageHeader eyebrow="Events" title={<>Investor <b>Symposium</b></>} />
        <EmptyState
          icon={<ShieldAlert size={40} />}
          title="Client Relations access required"
          hint="The Symposium console requires the crm.events.manage permission (Client Relations or CRM Admin role in WISE-IAM)."
        />
      </div>
    )
  }
  if (loading) return <div className="sym dim" style={{ padding: 40 }}>Loading Symposium…</div>
  if (error) {
    return (
      <div className="sym">
        <PageHeader eyebrow="Events" title={<>Investor <b>Symposium</b></>} />
        <EmptyState title="Could not load" hint={error} action={<Button variant="default" onClick={() => { setLoading(true); void load() }}>Retry</Button>} />
      </div>
    )
  }
  if (!bundle) {
    return (
      <div className="sym">
        <PageHeader
          eyebrow="Events"
          title={<>Investor <b>Symposium</b></>}
          sub="No Symposium event exists yet. Create the first one to start planning."
          actions={<Button variant="primary" icon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>Create event</Button>}
        />
        <EmptyState icon={<CalendarRange size={40} />} title="No event yet" hint="Everything — invite list, waves, agenda, playbook — hangs off the event record." />
        <EventModal open={createOpen} onClose={() => setCreateOpen(false)} onSaved={() => { setLoading(true); void load() }} />
      </div>
    )
  }

  const ev = bundle.event
  const daysToEvent = ev.eventDate
    ? Math.ceil((new Date(ev.eventDate).getTime() - Date.now()) / 86_400_000)
    : null

  return (
    <div className="sym">
      <PageHeader
        eyebrow="Events · Client Relations"
        title={<>{ev.year} Investor <b>Symposium</b></>}
        sub={`${ev.eventDate ? fmtDate(ev.eventDate, 'long') : 'Date TBD'} · ${ev.venueName ?? 'Venue TBD'} · capacity ${ev.capacity ?? '—'} · target ${ev.targetAttendance ?? '—'}${daysToEvent != null ? ` · ${daysToEvent} days out` : ''}`}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {mailbox?.available && (
              <span title={`Mail sitting in the events@ inbox — bounces are auto-filed and recorded on invites, so this is human mail awaiting a human. Last bounce scan: ${mailbox.lastScan ? new Date(mailbox.lastScan.at).toLocaleString() : 'not yet run'}`}>
                <Badge tone={(mailbox.inboxTotal ?? 0) > 0 ? 'warn' : 'neutral'} variant="outline">
                  events@ inbox: {mailbox.inboxTotal ?? 0}
                </Badge>
              </span>
            )}
            <Badge tone={bundle.emailEnabled ? 'warn' : 'neutral'} variant="outline">
              {bundle.emailEnabled ? 'Email sending ENABLED' : 'Email sending disabled'}
            </Badge>
            <Button variant="default" size="sm" icon={<Pencil size={14} />} onClick={() => setEditOpen(true)}>Edit event</Button>
          </div>
        }
      />
      <EventModal open={editOpen} onClose={() => setEditOpen(false)} onSaved={() => { setLoading(true); void load() }} event={ev} />
      <div className="sym-tabs"><Tabs tabs={visibleTabs} active={tab} onChange={(t) => setTab(t as TabId)} /></div>

      {tab === 'overview' && <OverviewTab bundle={bundle} invites={investorInvites} onDrill={drillToInvites} onDelivery={openDelivery} />}
      {tab === 'invites' && (
        <InvitesTab bundle={bundle} invites={investorInvites} staffInvites={staffInvites} allInvites={invites} contacts={contacts} contactById={contactById} accountById={accountById} refresh={load} filters={inviteFilters} setFilters={setInviteFilters} />
      )}
      {tab === 'rsvp' && <RsvpTab bundle={bundle} invites={investorInvites} contactById={contactById} accountById={accountById} refresh={load} />}
      {tab === 'delivery' && <DeliveryTab eventId={ev.id} eventYear={ev.year} waveId={deliveryWaveId} onWaveId={setDeliveryWaveId} trace={mailbox?.trace} />}
      {tab === 'agenda' && <AgendaTab bundle={bundle} refresh={load} />}
      {tab === 'playbook' && <PlaybookTab bundle={bundle} refresh={load} />}
      {tab === 'portal' && isAdmin && <PortalPreviewTab invites={investorInvites} contactById={contactById} accountById={accountById} event={ev} refresh={load} />}
      {tab === 'email' && isAdmin && <EmailPreviewTab bundle={bundle} refresh={load} />}
      {tab === 'recurring' && isAdmin && <RecurringTab bundle={bundle} />}
    </div>
  )
}

/* ============================ Create event ============================ */

// Create AND edit — pass `event` to edit the existing record (PATCH; year is
// immutable, the API's event PATCH map doesn't carry it).
function EventModal({ open, onClose, onSaved, event }: { open: boolean; onClose: () => void; onSaved: () => void; event?: any }) {
  const getToken = useApiToken()
  const year = new Date().getFullYear()
  const isoDay = (v: unknown) => (v ? String(v).slice(0, 10) : '')
  const blank = {
    year: String(year), title: `${year} Investor Symposium`,
    venueName: 'Big Canyon Country Club', venueAddress: '', capacity: '250',
    targetAttendance: '200', keynoteSpeaker: '', eventDate: '', rsvpDeadline: '',
  }
  const fromEvent = (ev: any) => ({
    year: String(ev.year), title: ev.title ?? '',
    venueName: ev.venueName ?? '', venueAddress: ev.venueAddress ?? '',
    capacity: ev.capacity != null ? String(ev.capacity) : '',
    targetAttendance: ev.targetAttendance != null ? String(ev.targetAttendance) : '',
    keynoteSpeaker: ev.keynoteSpeaker ?? '',
    eventDate: isoDay(ev.eventDate), rsvpDeadline: isoDay(ev.rsvpDeadline),
  })
  const [form, setForm] = useState(event ? fromEvent(event) : blank)
  useEffect(() => { if (open) setForm(event ? fromEvent(event) : blank) }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const save = async () => {
    const token = await getToken()
    const body = {
      title: form.title, venueName: form.venueName, venueAddress: form.venueAddress,
      keynoteSpeaker: form.keynoteSpeaker,
      capacity: form.capacity ? Number(form.capacity) : null,
      targetAttendance: form.targetAttendance ? Number(form.targetAttendance) : null,
      eventDate: form.eventDate || null,
      rsvpDeadline: form.rsvpDeadline || null,
    }
    if (event) await api.symposium.patchEvent(token, event.id, body)
    else await api.symposium.createEvent(token, { ...body, year: Number(form.year) })
    onClose(); onSaved()
  }
  return (
    <Modal open={open} onClose={onClose} title={event ? 'Edit Symposium event' : 'Create Symposium event'} footer={
      <><Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!form.title || !form.year} onClick={() => void save()}>{event ? 'Save' : 'Create'}</Button></>
    }>
      {!event && <Field label="Year" required><TextInput value={form.year} onChange={set('year')} /></Field>}
      <Field label="Title" required><TextInput value={form.title} onChange={set('title')} /></Field>
      <Field label="Event date" hint="Leave blank while TBD."><TextInput type="date" value={form.eventDate} onChange={set('eventDate')} /></Field>
      <Field label="RSVP deadline"><TextInput type="date" value={form.rsvpDeadline} onChange={set('rsvpDeadline')} /></Field>
      <Field label="Venue"><TextInput value={form.venueName} onChange={set('venueName')} /></Field>
      <Field label="Capacity"><TextInput value={form.capacity} onChange={set('capacity')} /></Field>
      <Field label="Target attendance"><TextInput value={form.targetAttendance} onChange={set('targetAttendance')} /></Field>
      <Field label="Keynote speaker"><TextInput value={form.keynoteSpeaker} onChange={set('keynoteSpeaker')} placeholder="TBD" /></Field>
    </Modal>
  )
}

/* ============================ Overview ============================ */

function OverviewTab({ bundle, invites, onDrill, onDelivery }: {
  bundle: EventBundle; invites: any[]; onDrill: (f: Partial<InviteFilters>) => void; onDelivery: (waveId: string) => void
}) {
  const { kpis, waves, event } = bundle
  // Investor seats against the target, because this number decides whether to
  // invite more investors. The whole room against the venue is HeadcountCard.
  const seatsOpen = (event.targetAttendance ?? 0) - kpis.rsvpYesSeats
  // The staff list sits below the whole Invitation List, so land on it.
  const openStaffList = () => {
    onDrill({})
    requestAnimationFrame(() => document.querySelector('.sym-wpi-staff')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }
  const nextUp = invites
    .filter((i) => i.tier === 2 && i.inviteStatus === 'pending_review')
    .slice(0, 5)
  return (
    <div className="sym-stack">
      <KpiGrid cols={6 as 2 | 3 | 4 | 5}>
        <KpiTile label="Master List" value={num(kpis.masterList)} sub="contacts on list" onClick={() => onDrill({})} />
        {/* "Invited" is a decision recorded in a dropdown, NOT a send receipt.
            Saying "invitations extended" under it claimed an action nobody had
            taken: 45 people sat marked invited having received nothing, Donna
            Waldo among them. The tile now names the gap and drills into it. */}
        <KpiTile
          label="Invited"
          value={num(kpis.invited)}
          sub={kpis.invitedNotEmailed > 0
            ? `${num(kpis.invitedNotEmailed)} not emailed yet`
            : 'all emailed'}
          onClick={() => onDrill(kpis.invitedNotEmailed > 0
            ? { status: 'invited', sent: 'not_sent' }
            : { status: 'invited' })}
        />
        <KpiTile label="People Coming" value={num(kpis.rsvpYesSeats)} accent sub={`${num(kpis.rsvpYes)} RSVPs + ${num(kpis.rsvpYesGuests)} guests`} onClick={() => onDrill({ rsvp: 'yes' })} />
        <KpiTile label="Declined" value={num(kpis.declined)} sub="rsvp no" onClick={() => onDrill({ rsvp: 'no' })} />
        <KpiTile label="Pending" value={num(kpis.pending)} sub="invited · no reply" onClick={() => onDrill({ status: 'invited', rsvp: 'none' })} />
        <KpiTile label="Data Issues" value={num(kpis.dataFlags)} sub="missing or bounced email" onClick={() => onDrill({ flag: 'flagged' })} />
        {kpis.needsResend > 0 && (
          <KpiTile
            label="Needs Resend"
            value={num(kpis.needsResend)}
            sub="address changed since sending"
            onClick={() => onDrill({ sent: 'needs_resend' })}
          />
        )}
      </KpiGrid>

      <HeadcountCard kpis={kpis} capacity={event.capacity} onOpenStaff={openStaffList} />

      <div className="sym-grid2">
        <SectionCard eyebrow="This year at a glance" title={<>Planning <b>Timeline</b></>}>
          {waves.length === 0 ? (
            <EmptyState title="No waves yet" hint="Draft invitation waves from the Invitation List tab — Save the Date, Keynote announced, Reminder, Final." />
          ) : (
            <div className="sym-timeline">
              {waves.map((w) => (
                <div key={w.id} className={`sym-tl-item sym-tl--${w.status}`}>
                  <span className="sym-tl-dot" />
                  <div>
                    <div className="sym-tl-name">Wave {w.waveNumber} — {w.name}</div>
                    <div className="sym-tl-sub dim" title={w.lastError ? `Last error: ${w.lastError}` : undefined}>
                      {/* Statuses come from the wave sender: draft → sending → sent | failed.
                          'sending'/'failed' used to fall through to "Draft", which
                          told Client Relations a half-delivered wave had never gone. */}
                      {w.status === 'sent' ? `Sent ${fmtDate(w.sentAt, 'med')} · ${w.sentCount ?? 0} recipients`
                        : w.status === 'sending' ? `Sending${w.startedAt ? ` since ${fmtDate(w.startedAt, 'med')}` : ''} · ${w.sentCount ?? 0} sent so far${w.targetCount != null ? ` of ${w.targetCount}` : ''}`
                          : w.status === 'failed' ? `Failed${w.finishedAt ? ` ${fmtDate(w.finishedAt, 'med')}` : ''} · ${w.sentCount ?? 0} sent · ${w.failedCount ?? 0} failed`
                            : w.scheduledAt ? `Scheduled ${fmtDate(w.scheduledAt, 'med')}` : 'Draft'}
                      {(w.status === 'sent' || w.status === 'sending' || w.status === 'failed') && (
                        <> · <button className="sym-tl-link" onClick={() => onDelivery(w.id)} title="What the message trace and the tracked link know about every recipient of this wave">Delivery report</button></>
                      )}
                    </div>
                  </div>
                  <Badge tone={w.status === 'sent' ? 'ok' : w.status === 'scheduled' || w.status === 'sending' ? 'info' : w.status === 'failed' ? 'alert' : 'neutral'} variant="soft">{w.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard eyebrow="Capacity & escalation" title={<>Seats <b>& Escalation</b></>}>
          <div className="sym-seats">
            <div className="sym-seat"><span className="sym-seat-v num">{num(event.targetAttendance ?? 0)}</span><span className="sym-seat-l">Target</span></div>
            <div className="sym-seat"><span className="sym-seat-v num">{num(kpis.rsvpYesSeats)}</span><span className="sym-seat-l">Investor seats</span></div>
            <div className="sym-seat"><span className={`sym-seat-v num ${seatsOpen !== 0 ? 'sym-open' : ''}`}>{num(Math.abs(seatsOpen))}</span><span className="sym-seat-l">{seatsOpen < 0 ? 'Over target' : 'Seats open'}</span></div>
          </div>
          <div className="sym-nextup">
            <div className="label" style={{ marginBottom: 6 }}>Next up (Tier 2, pending review)</div>
            {nextUp.length === 0
              ? <span className="dim" style={{ fontSize: 13 }}>No Tier-2 candidates awaiting review.</span>
              : nextUp.map((i) => <div key={i.id} className="sym-nextup-row">{i.contactId}</div>)}
            <p className="dim" style={{ fontSize: 12, marginTop: 8 }}>
              Promotions are always a human click on the Invitation List tab — the system only recommends.
            </p>
          </div>
        </SectionCard>
      </div>

      <SectionCard eyebrow="In-house · Microsoft 365" title={<>System <b>& Services</b></>}>
        <div className="sym-services">
          <div><Badge tone="navy" variant="soft">Email</Badge> {bundle.emailEnabled
            ? <>Microsoft Graph as <b>events@westportproperties.net</b> — single-invite sending live; wave delivery pending.</>
            : <>Microsoft Graph as events@westportproperties.net — <b>sending disabled (kill switch)</b>.</>}</div>
          <div><Badge tone="navy" variant="soft">Identity</Badge> Internal: Entra SSO + WISE-IAM roles. Investors: verified-email link (portal slice, upcoming).</div>
          <div><Badge tone="navy" variant="soft">Data</Badge> CRM database is the single source of truth — invitees reference CRM Contacts.</div>
          <div><Badge tone="navy" variant="soft">Calendar</Badge> Outlook/ICS hold on RSVP Yes (upcoming).</div>
        </div>
      </SectionCard>
    </div>
  )
}

/* ========================== Invitation List ========================== */

function InvitesTab({ bundle, invites, staffInvites, allInvites, contacts, contactById, accountById, refresh, filters, setFilters }: {
  bundle: EventBundle; invites: any[]; staffInvites: any[]; contacts: Contact[]
  /** EVERY invite row, including archived contacts. The two lists above are
   *  archived-filtered for the roster tables; the registration queue must not
   *  be the one place a person can ask to come and never be seen. */
  allInvites: any[]
  contactById: Map<string, Contact>; accountById: Map<string, Account>; refresh: () => Promise<void>
  filters: InviteFilters; setFilters: React.Dispatch<React.SetStateAction<InviteFilters>>
}) {
  const getToken = useApiToken()
  const [addOpen, setAddOpen] = useState(false)
  const [addStaffOpen, setAddStaffOpen] = useState(false)
  const [inviteStaffOpen, setInviteStaffOpen] = useState(false)
  const [waveOpen, setWaveOpen] = useState(false)
  const [mergeOpen, setMergeOpen] = useState(false)
  const [emailInvite, setEmailInvite] = useState<any | null>(null)
  // Both hooks called unconditionally — `a() || b()` short-circuits, which
  // would skip the second one on any render where the first is true.
  const canMergeContacts = useHasPermission('crm.contacts.merge')
  const canAdminContacts = useHasPermission('crm.manage')
  const canMerge = canMergeContacts || canAdminContacts

  const setF = (patch: Partial<InviteFilters>) => setFilters((f) => ({ ...f, ...patch }))
  const rows = useMemo(() => applyFilters(invites, filters, contactById, accountById), [invites, filters, contactById, accountById])
  const filtersActive = !!(filters.search.trim() || filters.status || filters.rsvp || filters.gift || filters.flag || filters.history || filters.sent || filters.tier != null)
  const histYears = historyYears(bundle.event.year)

  const patch = async (inviteId: string, body: object) => {
    const token = await getToken()
    try {
      await api.symposium.patchInvite(token, inviteId, body)
    } catch (err) {
      // A refused edit (say, a delivery on a gift another tab just set to No)
      // must not look like a save — say so, then show what is really stored.
      alert(`Not saved: ${err instanceof Error ? err.message : String(err)}`)
    }
    await refresh()
  }

  // Everyone still owed a gift in the post, whatever the filters above show —
  // a mailing list that silently followed a Tier filter would drop people.
  const mailingRows = useMemo(() => invites.filter((i) => i.giftNeedsMailing), [invites])
  const exportGiftMailing = () => {
    const rows = mailingRows
      .map((i) => ({ i, c: contactById.get(i.contactId) }))
      .sort((a, b) => (a.c?.lastName ?? '').localeCompare(b.c?.lastName ?? '')
        || (a.c?.firstName ?? '').localeCompare(b.c?.firstName ?? ''))
    downloadCsv(
      `symposium-${bundle.event.year}-gift-mailing-list.csv`,
      ['Last', 'Recipient Full Name', 'Company', 'Email', 'Address 1', 'Address 2', 'City', 'State', 'Zip',
        'Phone', 'Shirt size', 'Shirt cut', 'Gift delivery'],
      rows.map(({ i, c }) => {
        const a = mailingAddress(c, accountById)
        return [
          c?.lastName ?? '',
          c ? contactName(c) : '',
          companyFor(contactById, accountById, i) ?? '',
          c?.workEmail || c?.personalEmail || '',
          a?.line1 ?? '', a?.line2 ?? '', a?.city ?? '', a?.state ?? '', a?.zip ?? '',
          c?.officePhone || c?.cellPhone || '',
          c?.shirtSize ?? '',
          c?.shirtStyle === 'W' ? "Women's" : c?.shirtStyle === 'M' ? "Men's" : '',
          giftDeliveryLabel(i.giftDelivery),
        ]
      }),
    )
  }

  const cols: Column<any>[] = [
    {
      key: 'contact', header: 'Contact',
      sortValue: (i) => nameFor(contactById, i.contactId),
      // Deep link to the CRM record — Jason's ask: fix contact info (title,
      // company, email) from the invite list without hunting through Contacts.
      render: (i) => {
        const c = contactById.get(i.contactId)
        // missing_email resolves itself once the contact record gains one —
        // offer the one-click clear instead of silently keeping the flag.
        const resolved = i.dataFlag === 'missing_email' && !!(c?.workEmail || c?.personalEmail)
        return (
          <span className="sym-contact-cell">
            {c
              ? <Link className="sym-contact sym-contact--link" to={`/contacts/${i.contactId}`} title="Open the CRM contact record">{nameFor(contactById, i.contactId)}</Link>
              : <span className="sym-contact">{nameFor(contactById, i.contactId)}</span>}
            {i.dataFlag && (resolved
              ? <button className="sym-flag sym-flag--resolved" title="The contact record has an email now — click to clear the flag" onClick={() => void patch(i.id, { dataFlag: null })}>Email added ✓ clear</button>
              : i.dataFlag === 'bounced_email'
                ? <button className="sym-flag" title="The invitation email bounced — fix the contact's email (click the name), resend, or click to clear the flag" onClick={() => void patch(i.id, { dataFlag: null })}>Email bounced · clear</button>
                : <span className="sym-flag" title="Flagged at import — fix on the contact record (click the name)">{FLAG_LABEL[i.dataFlag] ?? i.dataFlag}</span>)}
          </span>
        )
      },
    },
    {
      key: 'group', header: 'Investor Group',
      sortValue: (i) => companyFor(contactById, accountById, i) ?? '',
      render: (i) => <span className="muted">{companyFor(contactById, accountById, i) ?? '—'}</span>,
    },
    {
      key: 'history', header: 'History', width: 142, align: 'center',
      sortValue: (i) => historySort(contactById.get(i.contactId)),
      render: (i) => <HistoryCell contact={contactById.get(i.contactId)} years={histYears} />,
    },
    {
      key: 'tier', header: 'Tier', width: 90, align: 'center', sortValue: (i) => i.tier ?? 9,
      render: (i) => (
        <select className="sym-inline-select" value={i.tier ?? ''} onChange={(e) => void patch(i.id, { tier: e.target.value ? Number(e.target.value) : null })}>
          <option value="">—</option><option value="1">1</option><option value="2">2</option><option value="3">3</option>
        </select>
      ),
    },
    { key: 'invitedBy', header: 'Invited By', sortValue: (i) => i.invitedBy ?? '', render: (i) => <span className="muted">{i.invitedBy ?? '—'}</span> },
    {
      key: 'status', header: 'Invite Status', width: 160, sortValue: (i) => i.inviteStatus,
      render: (i) => (
        <select className="sym-inline-select" value={i.inviteStatus} onChange={(e) => void patch(i.id, { inviteStatus: e.target.value })}>
          {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      ),
    },
    {
      key: 'rsvp', header: 'RSVP', width: 130, align: 'center', sortValue: (i) => i.rsvp,
      render: (i) => <RsvpSelect invite={i} onPatch={(b) => void patch(i.id, b)} />,
    },
    {
      // "Invited" is a DECISION a human made; this is whether we actually
      // emailed them. They were the same field until 2026-08-07, which hid
      // 183 people marked invited who had never received anything.
      key: 'sent', header: 'Invitation sent', width: 140, align: 'center',
      sortValue: (i) => (i.addressChanged ? 0.5 : i.emailSentAt ? 1 : i.inviteStatus === 'invited' ? 0 : -1),
      render: (i: any) => {
        // Mailed, but at an address that has since been corrected — so they
        // have never actually received a copy. A plain "sent" here is what
        // let corrected addresses sit in the skipped bucket of every wave
        // (2026-09-09); waves now include these automatically.
        if (i.addressChanged) {
          // A flag still on the row means the NEW address is not mailable
          // either (a bare word, say) — say "fix email", because a wave will
          // skip them for want of an address, not for want of a decision.
          const sendable = !i.dataFlag
          return (
            <span title={`Sent ${new Date(i.emailSentAt).toLocaleString()} to ${i.mailedTo} — the address has changed since, so that copy never reached them.${sendable ? ' The next wave will include them.' : ' The address on the contact now is not mailable — fix it first.'}`}>
              <Badge tone={sendable ? 'warn' : 'alert'} variant="soft" dot>
                {sendable ? 'needs resend' : 'fix email'}
              </Badge>
            </span>
          )
        }
        if (i.emailSentAt) {
          return (
            <span title={`Sent ${new Date(i.emailSentAt).toLocaleString()}${i.mailedTo ? ` to ${i.mailedTo}` : ''}`}>
              <Badge tone="ok" variant="soft" dot>sent</Badge>
            </span>
          )
        }
        if (i.inviteStatus === 'invited') {
          return <span title="Marked invited, but no invitation email has gone out"><Badge tone="warn" variant="soft" dot>not sent</Badge></span>
        }
        return <span className="muted">—</span>
      },
    },
    // Preview + Email (2026-09-09): Client Relations owns the event and now
    // sends from here. Was admin-only from 2026-07-30, which left the team
    // that runs the invitations unable to email anybody.
    {
      key: 'preview', header: '', width: 150, align: 'center', sortValue: () => 0,
      render: (i: any) => (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <button
            className="sym-mini sym-mini--wide"
            title="Preview the investor portal as this contact (read-only)"
            onClick={() => void (async () => {
              const token = await getToken()
              const r = await api.symposium.previewLink(token, i.id)
              window.open(r.path, '_blank', 'noopener')
            })()}
          >
            Preview
          </button>
          <button
            className="sym-mini sym-mini--wide"
            title="Compose this invitee's invitation email — send a test to yourself, or the real thing"
            onClick={() => setEmailInvite(i)}
          >
            Email
          </button>
        </span>
      ),
    },
    {
      key: 'gift', header: 'Gift / delivery', width: 180, className: 'sym-gift-td', sortValue: giftSortValue,
      render: (i) => <GiftCell invite={i} onPatch={(b) => void patch(i.id, b)} />,
    },
  ]

  const staffCols: Column<any>[] = [
    { key: 'contact', header: 'Name', sortValue: (i) => nameFor(contactById, i.contactId), render: (i) => <span className="sym-contact">{nameFor(contactById, i.contactId)}</span> },
    { key: 'title', header: 'Title', sortValue: (i) => contactById.get(i.contactId)?.title ?? '', render: (i) => <span className="muted">{contactById.get(i.contactId)?.title ?? '—'}</span> },
    {
      key: 'status', header: 'Working the event', width: 150, sortValue: (i) => i.inviteStatus,
      render: (i) => (
        <select className="sym-inline-select" value={i.inviteStatus} onChange={(e) => void patch(i.id, { inviteStatus: e.target.value })}>
          <option value="pending_review">TBD</option>
          <option value="invited">Yes</option>
          <option value="not_invited">No</option>
        </select>
      ),
    },
    {
      // Independent of the column beside it: running the event and being
      // invited to it are different answers to different questions.
      key: 'guest', header: 'Invited to attend', width: 130, align: 'center',
      sortValue: (i) => (i.staffGuest ? 1 : 0),
      render: (i) => (
        <input
          type="checkbox"
          checked={!!i.staffGuest}
          onChange={(e) => void patch(i.id, { staffGuest: e.target.checked })}
          aria-label="Invited to attend"
        />
      ),
    },
    {
      key: 'staffSent', header: 'Invitation sent', width: 130, align: 'center',
      sortValue: (i) => (i.emailSentAt ? 1 : 0),
      render: (i) => (i.emailSentAt
        ? <span title={`Sent ${new Date(i.emailSentAt).toLocaleString()}`}><Badge tone="ok" variant="soft" dot>sent</Badge></span>
        : i.staffGuest
          ? <span title="Invited to attend, but no invitation has gone out"><Badge tone="warn" variant="soft" dot>not sent</Badge></span>
          : <span className="muted">—</span>),
    },
    {
      key: 'staffRsvp', header: 'RSVP', width: 90, align: 'center',
      sortValue: (i) => i.rsvp ?? '',
      render: (i) => (i.staffGuest
        ? <Badge tone={i.rsvp === 'yes' ? 'ok' : i.rsvp === 'no' ? 'alert' : 'neutral'} variant="soft">{i.rsvp ?? 'none'}</Badge>
        : <span className="muted">—</span>),
    },
    {
      key: 'sent', header: 'Invitation', width: 110, align: 'center', sortValue: (i) => (i.invitedAt ? 1 : 0),
      render: (i: any) => (i.invitedAt
        ? <Badge tone="ok" variant="soft" dot>sent</Badge>
        : <span className="muted">—</span>),
    },
    // WPI staff receive the SAME invitation as investors (Chauncey
    // 2026-08-07) so they see exactly what was sent out. Sending here does
    // NOT touch "Working the event" — that column is staffing, not RSVP.
    {
      key: 'actions', header: '', width: 150, align: 'center', sortValue: () => 0,
      render: (i: any) => (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <button
            className="sym-mini sym-mini--wide"
            title="Preview the investor portal as this team member (read-only)"
            onClick={() => void (async () => {
              const token = await getToken()
              const r = await api.symposium.previewLink(token, i.id)
              window.open(r.path, '_blank', 'noopener')
            })()}
          >
            Preview
          </button>
          <button
            className="sym-mini sym-mini--wide"
            title="Send this team member the investor invitation"
            onClick={() => setEmailInvite(i)}
          >
            Email
          </button>
        </span>
      ),
    },
  ]

  return (
    <div className="sym-stack">
      {/* Above the list on purpose: a person waiting on a decision outranks
          the roster they are not yet on. Hides itself when the queue is empty. */}
      <PendingRegistrations invites={allInvites} contactById={contactById} refresh={refresh} />

      <SectionCard
        eyebrow="One list · one source of truth"
        title={<>Invitation <b>List</b></>}
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            {canMerge && (
              <Button variant="default" size="sm" icon={<GitMerge size={14} />} onClick={() => setMergeOpen(true)}>
                Review duplicates
              </Button>
            )}
            <Button variant="default" size="sm" icon={<Plus size={14} />} onClick={() => setAddOpen(true)}>Add contact</Button>
            <span title={mailingRows.length
              ? `${num(mailingRows.length)} with Gift Yes, not yet picked up or mailed — everyone, whatever the filters below show`
              : 'Nobody is waiting on a mailed gift: no Gift Yes without “Picked up” or “Mailed”'}>
              <Button variant="default" size="sm" icon={<Download size={14} />} disabled={!mailingRows.length} onClick={exportGiftMailing}>
                Gift mailing list (CSV){mailingRows.length ? ` · ${num(mailingRows.length)}` : ''}
              </Button>
            </span>
            <Button variant="primary" size="sm" icon={<Send size={14} />} onClick={() => setWaveOpen(true)}>Send invitation wave</Button>
          </div>
        }
      >
        <ListToolbar
          views={[
            { id: 'all', label: 'All' },
            { id: '1', label: 'Tier 1' },
            { id: '2', label: 'Tier 2' },
            { id: '3', label: 'Tier 3' },
          ]}
          activeView={filters.tier == null ? 'all' : String(filters.tier)}
          onView={(id) => setF({ tier: id === 'all' ? null : Number(id) })}
          search={filters.search}
          onSearch={(v) => setF({ search: v })}
          searchPlaceholder="Search name, company, or email…"
          count={rows.length}
          extra={
            <div className="sym-filters">
              <select className={`sym-filter${filters.status ? ' on' : ''}`} value={filters.status} onChange={(e) => setF({ status: e.target.value })}>
                <option value="">Status</option>
                {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <select className={`sym-filter${filters.rsvp ? ' on' : ''}`} value={filters.rsvp} onChange={(e) => setF({ rsvp: e.target.value })}>
                <option value="">RSVP</option>
                <option value="yes">Yes</option><option value="maybe">Maybe</option>
                <option value="no">Declined</option><option value="none">No reply</option>
              </select>
              <select className={`sym-filter${filters.gift ? ' on' : ''}`} value={filters.gift} onChange={(e) => setF({ gift: e.target.value })}>
                <option value="">Gift</option>
                <option value="yes">Gift: Yes</option>
                <option value="no">Gift: No</option>
                <option value="needs_mailing">Gift: needs mailing</option>
                <option value="picked_up">Gift: picked up</option>
                <option value="mailed">Gift: mailed</option>
              </select>
              <select className={`sym-filter${filters.history ? ' on' : ''}`} value={filters.history} onChange={(e) => setF({ history: e.target.value })}>
                <option value="">History</option>
                <optgroup label="Attended">
                  {histYears.slice().reverse().map((y) => <option key={y} value={`att:${y}`}>Attended ’{String(y).slice(2)}</option>)}
                  <option value="att:any">Attended any year</option>
                  <option value="att:never">Never attended</option>
                </optgroup>
                <optgroup label="Invited">
                  {histYears.slice().reverse().map((y) => <option key={y} value={`inv:${y}`}>Invited ’{String(y).slice(2)}</option>)}
                  <option value="inv:never">First-time (never on a list)</option>
                </optgroup>
              </select>
              <select className={`sym-filter${filters.sent ? ' on' : ''}`} value={filters.sent} onChange={(e) => setF({ sent: e.target.value })}>
                <option value="">Mailing</option>
                <option value="sent">Emailed</option>
                <option value="not_sent">Never emailed</option>
                <option value="needs_resend">Needs resend (address corrected)</option>
              </select>
              <select className={`sym-filter${filters.flag ? ' on' : ''}`} value={filters.flag} onChange={(e) => setF({ flag: e.target.value })}>
                <option value="">Data issues</option>
                <option value="flagged">Needs cleanup (missing email)</option><option value="clean">No issues</option>
              </select>
              {filtersActive && (
                <button className="sym-clear" onClick={() => setFilters(EMPTY_FILTERS)}>Clear</button>
              )}
            </div>
          }
        />
        {rows.length === 0
          ? (filtersActive
              ? <EmptyState title="No matches" hint="No invitees match the current search/filters — clear them to see the full list." />
              : <EmptyState title="No invitees yet" hint="Add contacts from the CRM contact list — the imported master list will land here." />)
          : (
            <>
              <div className="sym-hist-legend">
                <span>History — one box per year ({`’${String(histYears[0]).slice(2)}–’${String(histYears[histYears.length - 1]).slice(2)}`}):</span>
                <span className="sym-hist-y sym-hist-y--att">’24</span> attended that year
                <span className="sym-hist-y sym-hist-y--inv">’24</span> invited, didn’t attend
              </div>
              <DataTable rows={rows} columns={cols} getRowKey={(i: any) => i.id} />
            </>
          )}

        <AddInviteModal open={addOpen} onClose={() => setAddOpen(false)} contacts={contacts} existing={new Set([...invites, ...staffInvites].map((i) => i.contactId))} eventId={bundle.event.id} onDone={refresh} />
        <AddInviteModal open={addStaffOpen} onClose={() => setAddStaffOpen(false)} contacts={contacts} existing={new Set([...invites, ...staffInvites].map((i) => i.contactId))} eventId={bundle.event.id} onDone={refresh} staffOnly />
        <WaveModal open={waveOpen} onClose={() => setWaveOpen(false)} bundle={bundle} contactById={contactById} onDone={refresh} />
        {/* A merge repoints invitations onto the surviving contact, so the
            list behind this modal is stale the moment one happens — reload on
            close rather than leaving a row pointing at a deleted person. */}
        {canMerge && (
          <MergeDuplicatesModal
            open={mergeOpen}
            onClose={() => { setMergeOpen(false); void refresh() }}
          />
        )}
        {emailInvite && (
          <EmailInviteModal
            invite={emailInvite} contact={contactById.get(emailInvite.contactId)}
            bundle={bundle} onClose={() => setEmailInvite(null)} refresh={refresh}
          />
        )}
      </SectionCard>

      <SuggestedInvitees
        invites={invites} staffInvites={staffInvites} contacts={contacts}
        contactById={contactById} accountById={accountById}
        eventId={bundle.event.id} refresh={refresh}
      />

      <SectionCard
        className="sym-wpi-staff"
        eyebrow="Internal · counted separately from the investor funnel"
        title={<>WPI <b>Staff</b></>}
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="default" size="sm" icon={<Plus size={14} />} onClick={() => setAddStaffOpen(true)}>Add one</Button>
            <Button variant="navy" size="sm" icon={<Users size={14} />} onClick={() => setInviteStaffOpen(true)}>Invite employees</Button>
          </div>
        }
      >
        {staffInvites.length === 0
          ? <EmptyState title="No staff listed" hint="Add WPI team members working the event, or invite employees to attend." />
          : (
            <>
              <p className="sym-staff-note">
                <b>{num(bundle.kpis.staffWorking)}</b> working the event ·{' '}
                <b>{num(bundle.kpis.staffWorkingTbd)}</b> still TBD ·{' '}
                <b>{num(bundle.kpis.staffGuests)}</b> invited to attend ·{' '}
                <b>{num(bundle.kpis.staffGuestsEmailed)}</b> emailed ·{' '}
                <b>{num(bundle.kpis.staffRsvpYes)}</b> said yes.
                {' '}<b>Working the event</b> and <b>Invited to attend</b> are separate questions —
                someone can be both. Only people ticked as invited are reachable by a staff
                invitation wave, and none of these numbers touch the investor funnel.
              </p>
              <DataTable rows={staffInvites} columns={staffCols} getRowKey={(i: any) => i.id} />
            </>
          )}
        <InviteStaffModal
          open={inviteStaffOpen}
          onClose={() => setInviteStaffOpen(false)}
          contacts={contacts}
          alreadyGuests={new Set(staffInvites.filter((i: any) => i.staffGuest).map((i: any) => i.contactId))}
          eventId={bundle.event.id}
          onDone={refresh}
        />
      </SectionCard>
    </div>
  )
}

/* ======================== Registrations awaiting review ======================== */

// The queue for /symposium/rsvp (2026-09-16). Everything Drew sends that link
// to lands here, and NOTHING it contains can reach the portal or the headcount
// until someone in this room decides — pending_review rows cannot sign in
// (api/src/services/symposium-portal-access.ts).
//
// Approve does three things at once, which is exactly why it is a button and
// not "set the dropdown to Invited": marks them invited, records the RSVP they
// already gave (and the +1 if they asked for one), and sends the confirmation
// carrying the calendar file. The section hides itself when the queue is empty.
function PendingRegistrations({ invites, contactById, refresh }: {
  invites: any[]
  contactById: Map<string, Contact>
  refresh: () => Promise<void>
}) {
  const getToken = useApiToken()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const pending = useMemo(
    () => invites
      .filter((i) => i.registeredAt && i.inviteStatus === 'pending_review')
      .sort((a, b) => String(b.registeredAt).localeCompare(String(a.registeredAt))),
    [invites],
  )

  if (!pending.length) return null

  const decide = async (invite: any, approve: boolean) => {
    setBusyId(invite.id); setNote(null)
    try {
      const token = await getToken()
      if (approve) {
        const r = await api.symposium.approveRegistration(token, invite.id)
        // Told plainly, because "approved but we could not write to them" is a
        // different state of the world from "approved and confirmed", and only
        // one of them needs a phone call.
        setNote(r.confirmationSent
          ? `${nameOf(invite, contactById)} approved — confirmation and calendar invite sent.`
          : `${nameOf(invite, contactById)} approved, but the confirmation did NOT send${r.mailDetail ? ` (${r.mailDetail})` : ''}. Reach out directly.`)
      } else {
        await api.symposium.declineRegistration(token, invite.id)
        setNote(`${nameOf(invite, contactById)} declined. Nothing was emailed to them.`)
      }
      await refresh()
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <SectionCard
      eyebrow="Self-registered · nobody here is admitted until you say so"
      title={<>Registrations <b>awaiting review</b></>}
      right={<Badge tone="warn" variant="soft">{num(pending.length)} waiting</Badge>}
    >
      {note && <p className="sym-hint" style={{ marginBottom: 10 }}>{note}</p>}
      <div className="sym-reg-list">
        {pending.map((i) => {
          const c = contactById.get(i.contactId)
          const email = c?.workEmail || c?.personalEmail || null
          const forwarder = i.forwardedBy ? contactById.get(i.forwardedBy) : undefined
          return (
            <div className="sym-reg" key={i.id}>
              <div className="sym-reg-who">
                <div className="sym-reg-name">
                  {nameOf(i, contactById)}
                  {i.registrationPartySize === 2 && <Badge tone="navy" variant="soft">+1 guest</Badge>}
                  {i.registrationChannel === 'forwarded_link' && (
                    <Badge tone="neutral" variant="soft">Forwarded invitation</Badge>
                  )}
                  {/* An employee who registers is a GUEST, not staffing the
                      event — approving sets staff_guest, not the rota. */}
                  {c?.isWpi && <Badge tone="navy" variant="soft">WPI staff</Badge>}
                </div>
                <div className="sym-reg-meta">
                  {email ?? <span className="sym-reg-warn">no email address</span>}
                  {i.investorGroup ? ` · ${i.investorGroup}` : ''}
                  {i.registeredAt ? ` · registered ${fmtDate(i.registeredAt)}` : ''}
                </div>
                {/* Whoever forwarded the invitation is usually the deciding
                    context — it says which investor vouched for them. */}
                {forwarder && (
                  <div className="sym-reg-meta">Arrived on {contactName(forwarder)}&rsquo;s invitation link</div>
                )}
                {i.registrationNote && <div className="sym-reg-note">&ldquo;{i.registrationNote}&rdquo;</div>}
              </div>
              <div className="sym-reg-actions">
                <Button
                  variant="primary" size="sm" disabled={busyId === i.id}
                  onClick={() => void decide(i, true)}
                >
                  {busyId === i.id ? 'Working…' : 'Approve'}
                </Button>
                <Button variant="default" size="sm" disabled={busyId === i.id} onClick={() => void decide(i, false)}>
                  Decline
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    </SectionCard>
  )
}

/** The name on a registration: the contact record if we matched one, else
 *  whatever they typed — never a bare id in front of a reviewer. */
function nameOf(invite: any, contactById: Map<string, Contact>): string {
  const c = contactById.get(invite.contactId)
  return c ? contactName(c) : String(invite.contactId)
}

/* ========================== Suggested invitees ========================== */

// Contacts at accounts that are already represented on the invite list but
// who have no invite row themselves (Jason 7/28: Justin Simmons — Invesco —
// was invisible because the list is the imported roster, not a query).
// One click adds them as pending_review; the section hides itself when empty.
function SuggestedInvitees({ invites, staffInvites, contacts, contactById, accountById, eventId, refresh }: {
  invites: any[]; staffInvites: any[]; contacts: Contact[]
  contactById: Map<string, Contact>; accountById: Map<string, Account>
  eventId: string; refresh: () => Promise<void>
}) {
  const getToken = useApiToken()
  const [expanded, setExpanded] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const suggested = useMemo(() => {
    const invitedAccounts = new Set<string>()
    const existing = new Set<string>()
    for (const i of [...invites, ...staffInvites]) {
      existing.add(i.contactId)
      const c = contactById.get(i.contactId)
      if (c?.accountId) invitedAccounts.add(c.accountId)
    }
    return contacts
      .filter((c) =>
        !existing.has(c.id) && !c.isWpi && !c.archived &&
        c.accountId && invitedAccounts.has(c.accountId))
      .map((c) => ({ contact: c, account: accountById.get(c.accountId!)?.name ?? '—' }))
      .sort((a, b) => a.account.localeCompare(b.account) || contactName(a.contact).localeCompare(contactName(b.contact)))
  }, [invites, staffInvites, contacts, contactById, accountById])

  if (!suggested.length) return null
  const shown = expanded ? suggested : suggested.slice(0, 8)

  const add = async (contactId: string) => {
    setBusyId(contactId)
    try {
      const token = await getToken()
      await api.symposium.addInvite(token, eventId, { contactId, tier: null })
      await refresh()
    } finally {
      setBusyId(null)
    }
  }

  return (
    <SectionCard
      eyebrow="At an invited account · not on the list"
      title={<>Suggested <b>Invitees</b> <span className="sym-suggest-count">{suggested.length}</span></>}
    >
      <p className="sym-suggest-hint">
        These CRM contacts belong to accounts that already have people on the invitation list.
        Adding lands them as <b>Pending review</b> — nothing is sent.
      </p>
      <div className="sym-suggest-grid">
        {shown.map(({ contact: c, account }) => (
          <div key={c.id} className="sym-suggest-row">
            <div className="sym-suggest-who">
              <Link className="sym-contact sym-contact--link" to={`/contacts/${c.id}`}>{contactName(c)}</Link>
              <span className="muted"> — {account}{c.title ? ` · ${c.title}` : ''}</span>
            </div>
            <Button
              variant="default" size="sm" icon={<UserPlus size={13} />}
              disabled={busyId === c.id}
              onClick={() => void add(c.id)}
            >
              {busyId === c.id ? 'Adding…' : 'Add'}
            </Button>
          </div>
        ))}
      </div>
      {suggested.length > 8 && (
        <button className="sym-clear" style={{ marginTop: 8 }} onClick={() => setExpanded((e) => !e)}>
          {expanded ? 'Show fewer' : `Show all ${suggested.length}`}
        </button>
      )}
    </SectionCard>
  )
}

/* ============================ Portal preview ============================ */

// The investor portal is served by this same app at /symposium, so it embeds
// as a plain same-origin iframe. Each contact selection mints a fresh
// read-only preview session (same links the per-row Preview buttons open).
function PortalPreviewTab({ invites, contactById, accountById, event, refresh }: { invites: any[]; contactById: Map<string, Contact>; accountById: Map<string, Account>; event: any; refresh: () => void | Promise<void> }) {
  const getToken = useApiToken()
  const [filter, setFilter] = useState('')
  const [inviteId, setInviteId] = useState('')
  const [src, setSrc] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [mobile, setMobile] = useState(false)
  const [editContent, setEditContent] = useState(false)

  const sorted = useMemo(
    () => [...invites].sort((a, b) => nameFor(contactById, a.contactId).localeCompare(nameFor(contactById, b.contactId))),
    [invites, contactById],
  )
  const shown = filter
    ? sorted.filter((i) => `${nameFor(contactById, i.contactId)} ${companyFor(contactById, accountById, i) ?? ''}`.toLowerCase().includes(filter.toLowerCase()))
    : sorted

  const preview = useCallback(async (id: string) => {
    setInviteId(id); setErr(null); setSrc(null)
    try {
      const token = await getToken()
      const r = await api.symposium.previewLink(token, id)
      setSrc(r.path)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [getToken])

  useEffect(() => {
    if (!inviteId && sorted.length) void preview(sorted[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted.length])

  const current = sorted.find((i) => i.id === inviteId)

  return (
    <SectionCard
      eyebrow="Exactly what the investor sees"
      title={<>Investor Portal <b>Preview</b></>}
      right={
        <div className="sym-preview-controls">
          <Button variant={editContent ? 'primary' : 'default'} size="sm" icon={<Pencil size={14} />} onClick={() => setEditContent((o) => !o)}>
            {editContent ? 'Close editor' : 'Edit page content'}
          </Button>
          <button className={`chip${!mobile ? ' active' : ''}`} onClick={() => setMobile(false)} title="Desktop width"><Monitor size={13} /> Desktop</button>
          <button className={`chip${mobile ? ' active' : ''}`} onClick={() => setMobile(true)} title="Phone width"><Smartphone size={13} /> Mobile</button>
          <Button variant="default" size="sm" icon={<ExternalLink size={14} />} disabled={!src} onClick={() => { if (src) window.open(src, '_blank', 'noopener') }}>
            Open in new tab
          </Button>
        </div>
      }
    >
      {editContent && (
        <PortalContentEditor
          event={event}
          onSaved={() => {
            // Refresh the bundle (new portalContent) AND re-mint the preview
            // session — a fresh src remounts the iframe, so the saved copy is
            // visible immediately, exactly as an investor would see it.
            void refresh()
            if (inviteId) void preview(inviteId)
          }}
        />
      )}
      <div className="sym-preview-controls" style={{ marginBottom: 12 }}>
        <div className="toolbar-search" style={{ maxWidth: 240 }}>
          <Search size={15} />
          <input placeholder="Filter by name or group…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
        <select
          className="sym-inline-select sym-preview-select"
          value={inviteId}
          onChange={(e) => void preview(e.target.value)}
        >
          {shown.map((i) => (
            <option key={i.id} value={i.id}>
              {nameFor(contactById, i.contactId)}{companyFor(contactById, accountById, i) ? ` — ${companyFor(contactById, accountById, i)}` : ''}
            </option>
          ))}
        </select>
        {current && <span className="muted" style={{ fontSize: 12.5 }}>Read-only preview session · RSVP disabled</span>}
      </div>
      {err && <EmptyState title="Could not mint a preview session" hint={err} action={<Button variant="default" onClick={() => void preview(inviteId)}>Retry</Button>} />}
      {!err && (
        <div className="sym-preview-frame-wrap">
          {src
            ? <iframe key={src} src={src} className={`sym-preview-frame${mobile ? ' sym-preview-frame--mobile' : ''}`} title="Investor portal preview" />
            : <div className="dim" style={{ padding: 40 }}>Preparing preview…</div>}
        </div>
      )}
    </SectionCard>
  )
}

/* ---- Landing-page content editor (Jason, 2026-08-17) --------------------
   Edits crm.symposium_events.portal_content via the normal event PATCH.
   Every field is optional: empty = the page's built-in default (shown as the
   placeholder), so a partial edit never blanks the rest of the page. The
   next wave/reminder needs no changes — invite links always land on the
   live portal, which serves whatever is saved here. */
interface ContentDraft {
  eyebrow: string; welcomeMessage: string; rsvpHeading: string
  rsvpYesLabel: string; rsvpPlusOneLabel: string; rsvpMaybeLabel: string; rsvpNoLabel: string
  agendaPlaceholder: string
  details: { label: string; value: string }[]
  bgOverlayColor: string
  bgOverlayOpacity: number
  titleSizePx: number
  titleWeight: number
}
const CONTENT_TEXT_KEYS = [
  'eyebrow', 'welcomeMessage', 'rsvpHeading',
  'rsvpYesLabel', 'rsvpPlusOneLabel', 'rsvpMaybeLabel', 'rsvpNoLabel',
  'agendaPlaceholder',
] as const

/* Overlay tint painted over the background image. The defaults reproduce the
   original hardcoded soft-light wash — matching values are OMITTED on save so
   an untouched tint keeps riding the page's built-in default. Swatches are the
   Westport Properties Brand Color Palette (Jason's PDF, 2026-08-18). */
const OVERLAY_DEFAULT_COLOR = '#F4F6F9'
const OVERLAY_DEFAULT_OPACITY = 82
const OVERLAY_SWATCHES: { hex: string; name: string }[] = [
  { hex: '#F4F6F9', name: 'Soft light (default)' },
  { hex: '#FFFFFF', name: 'White' },
  { hex: '#0C2340', name: 'Navy · PMS 289' },
  { hex: '#3C5070', name: 'Layered navy · PMS 289 75%' },
  { hex: '#004B87', name: 'Blue · PMS 301' },
  { hex: '#5D93BF', name: 'Sky · PMS 301 60%' },
  { hex: '#00AEEF', name: 'Cyan · Process Cyan' },
  { hex: '#98A4AE', name: 'Platinum · PMS 7543' },
  { hex: '#425563', name: 'Slate · PMS 7545' },
]

/* Hero title style — defaults are the page's original 34px / Light 300.
   Weights are limited to the Source Serif 4 files the portal bundles. */
const TITLE_DEFAULT_SIZE = 34
const TITLE_DEFAULT_WEIGHT = 300
const TITLE_SIZE_MIN = 24
const TITLE_SIZE_MAX = 56
const TITLE_WEIGHTS: { value: number; label: string }[] = [
  { value: 300, label: 'Light' },
  { value: 400, label: 'Regular' },
  { value: 600, label: 'Semibold' },
  { value: 700, label: 'Bold' },
]

function overlayRgba(hex: string, pct: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${pct / 100})`
}

function toContentDraft(pc: Record<string, unknown> | null | undefined): ContentDraft {
  const p = (pc ?? {}) as Record<string, unknown>
  const s = (v: unknown) => (typeof v === 'string' ? v : '')
  return {
    eyebrow: s(p.eyebrow), welcomeMessage: s(p.welcomeMessage), rsvpHeading: s(p.rsvpHeading),
    rsvpYesLabel: s(p.rsvpYesLabel), rsvpPlusOneLabel: s(p.rsvpPlusOneLabel),
    rsvpMaybeLabel: s(p.rsvpMaybeLabel), rsvpNoLabel: s(p.rsvpNoLabel),
    agendaPlaceholder: s(p.agendaPlaceholder),
    details: Array.isArray(p.details)
      ? (p.details as { label?: string; value?: string }[]).map((r) => ({ label: r.label ?? '', value: r.value ?? '' }))
      : [],
    bgOverlayColor: /^#[0-9a-fA-F]{6}$/.test(s(p.bgOverlayColor)) ? s(p.bgOverlayColor).toUpperCase() : OVERLAY_DEFAULT_COLOR,
    bgOverlayOpacity: typeof p.bgOverlayOpacity === 'number'
      ? Math.min(100, Math.max(0, Math.round(p.bgOverlayOpacity)))
      : OVERLAY_DEFAULT_OPACITY,
    titleSizePx: typeof p.titleSizePx === 'number'
      ? Math.min(TITLE_SIZE_MAX, Math.max(TITLE_SIZE_MIN, Math.round(p.titleSizePx)))
      : TITLE_DEFAULT_SIZE,
    titleWeight: TITLE_WEIGHTS.some((w) => w.value === p.titleWeight) ? (p.titleWeight as number) : TITLE_DEFAULT_WEIGHT,
  }
}

function PortalContentEditor({ event, onSaved }: { event: any; onSaved: () => void }) {
  const getToken = useApiToken()
  const [d, setD] = useState<ContentDraft>(() => toContentDraft(event.portalContent))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const [bgBusy, setBgBusy] = useState(false)
  const [bgErr, setBgErr] = useState<string | null>(null)
  const bgThumbUrl = event.portalBgUrl ? `${API_BASE.replace(/\/api$/, '')}${event.portalBgUrl}` : null

  const uploadBg = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) { setBgErr('Image is larger than 5 MB — resize it and try again.'); return }
    setBgBusy(true); setBgErr(null)
    try {
      const token = await getToken()
      await api.symposium.uploadPortalBackground(token, event.id, file)
      onSaved()
    } catch (e) {
      setBgErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBgBusy(false)
    }
  }

  const removeBg = async () => {
    setBgBusy(true); setBgErr(null)
    try {
      const token = await getToken()
      await api.symposium.removePortalBackground(token, event.id)
      onSaved()
    } catch (e) {
      setBgErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBgBusy(false)
    }
  }
  const set = <K extends keyof ContentDraft>(k: K, v: ContentDraft[K]) => { setSaved(false); setD((p) => ({ ...p, [k]: v })) }
  const setDetail = (i: number, patch: Partial<{ label: string; value: string }>) =>
    set('details', d.details.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

  const save = async () => {
    setBusy(true); setErr(null)
    try {
      const token = await getToken()
      const content: Record<string, unknown> = {}
      for (const k of CONTENT_TEXT_KEYS) if (d[k].trim()) content[k] = d[k].trim()
      const details = d.details
        .map((r) => ({ label: r.label.trim(), value: r.value.trim() }))
        .filter((r) => r.label && r.value)
      if (details.length) content.details = details
      if (d.bgOverlayColor !== OVERLAY_DEFAULT_COLOR) content.bgOverlayColor = d.bgOverlayColor
      if (d.bgOverlayOpacity !== OVERLAY_DEFAULT_OPACITY) content.bgOverlayOpacity = d.bgOverlayOpacity
      if (d.titleSizePx !== TITLE_DEFAULT_SIZE) content.titleSizePx = d.titleSizePx
      if (d.titleWeight !== TITLE_DEFAULT_WEIGHT) content.titleWeight = d.titleWeight
      await api.symposium.patchEvent(token, event.id, {
        portalContent: Object.keys(content).length ? content : null,
      })
      setSaved(true)
      onSaved()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sym-content-editor">
      <p className="muted" style={{ margin: '0 0 10px', fontSize: 12.5 }}>
        This copy is what invitees see when they open their invitation link. Leave a field empty to
        use the built-in default (shown greyed). Saving updates the live page — the preview below
        reloads with your changes, and the next wave or reminder lands on exactly this page.
      </p>
      <div className="sym-content-grid">
        <Field label="Eyebrow (name is appended automatically)">
          <TextInput value={d.eyebrow} onChange={(e) => set('eyebrow', e.target.value)} placeholder="You’re invited" />
        </Field>
        <Field label="RSVP card heading">
          <TextInput value={d.rsvpHeading} onChange={(e) => set('rsvpHeading', e.target.value)} placeholder="Will you join us?" />
        </Field>
        <Field label="Welcome message (optional paragraph under the title)">
          <TextArea rows={2} value={d.welcomeMessage} onChange={(e) => set('welcomeMessage', e.target.value)} placeholder="None — add a short personal welcome here" />
        </Field>
        <Field label="Agenda placeholder (shown until the agenda is published)">
          <TextArea rows={2} value={d.agendaPlaceholder} onChange={(e) => set('agendaPlaceholder', e.target.value)} placeholder="The full agenda is coming soon — check back closer to the event." />
        </Field>
      </div>
      <div className="sym-content-details">
        <div className="label" style={{ marginBottom: 6 }}>
          Title style — size and weight of the event title (helps it stand out over a busy background)
        </div>
        <div className="sym-content-title-row">
          <div className="sym-content-wbtns" role="group" aria-label="Title weight">
            {TITLE_WEIGHTS.map((w) => (
              <button
                key={w.value}
                type="button"
                className={d.titleWeight === w.value ? 'sym-content-wbtn sym-content-wbtn--on' : 'sym-content-wbtn'}
                style={{ fontWeight: w.value }}
                aria-pressed={d.titleWeight === w.value}
                onClick={() => set('titleWeight', w.value)}
              >{w.label}</button>
            ))}
          </div>
          <label className="sym-content-tint-opacity">
            Size
            <input
              type="range"
              min={TITLE_SIZE_MIN}
              max={TITLE_SIZE_MAX}
              step={1}
              value={d.titleSizePx}
              onChange={(e) => set('titleSizePx', Number(e.target.value))}
            />
            <span className="sym-content-tint-pct">{d.titleSizePx}px</span>
          </label>
        </div>
        <div className="sym-content-title-sample" style={{ fontSize: d.titleSizePx, fontWeight: d.titleWeight }}>
          {event.title || 'Investor Symposium'}
        </div>
      </div>
      <div className="sym-content-grid sym-content-grid--4">
        <Field label="“Yes” button"><TextInput value={d.rsvpYesLabel} onChange={(e) => set('rsvpYesLabel', e.target.value)} placeholder="Yes, I’ll attend" /></Field>
        <Field label="“+1” button"><TextInput value={d.rsvpPlusOneLabel} onChange={(e) => set('rsvpPlusOneLabel', e.target.value)} placeholder="Yes, +1 guest" /></Field>
        <Field label="“Maybe” button"><TextInput value={d.rsvpMaybeLabel} onChange={(e) => set('rsvpMaybeLabel', e.target.value)} placeholder="Maybe" /></Field>
        <Field label="“No” button"><TextInput value={d.rsvpNoLabel} onChange={(e) => set('rsvpNoLabel', e.target.value)} placeholder="Can’t make it" /></Field>
      </div>
      <div className="sym-content-details">
        <div className="label" style={{ marginBottom: 6 }}>
          Details rows — shown between Venue and Questions (default: Attire · Business casual)
        </div>
        {d.details.map((r, i) => (
          <div key={i} className="sym-content-detail-row">
            <TextInput value={r.label} onChange={(e) => setDetail(i, { label: e.target.value })} placeholder="Label (e.g. Attire)" />
            <TextInput value={r.value} onChange={(e) => setDetail(i, { value: e.target.value })} placeholder="Value (e.g. Golf casual)" />
            <Button variant="ghost" onClick={() => set('details', d.details.filter((_, idx) => idx !== i))}>Remove</Button>
          </div>
        ))}
        <Button
          variant="default"
          size="sm"
          icon={<Plus size={14} />}
          disabled={d.details.length >= 12}
          onClick={() => set('details', [...d.details, { label: '', value: '' }])}
        >
          Add details row
        </Button>
      </div>
      <div className="sym-content-details">
        <div className="label" style={{ marginBottom: 6 }}>
          Background image — shown behind the whole landing page under the tint below (JPEG/PNG/WebP, max 5 MB)
        </div>
        <div className="sym-content-bg-row">
          {bgThumbUrl
            ? (
              <span className="sym-content-bg-thumbwrap" role="img" aria-label="Current landing-page background with the tint applied">
                <img src={bgThumbUrl} alt="" className="sym-content-bg-thumb" />
                <span className="sym-content-bg-tint" style={{ background: overlayRgba(d.bgOverlayColor, d.bgOverlayOpacity) }} />
              </span>
            )
            : <span className="muted" style={{ fontSize: 12.5 }}>None — the page uses its plain light background.</span>}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void uploadBg(f) }}
          />
          <Button variant="default" size="sm" disabled={bgBusy} onClick={() => fileRef.current?.click()}>
            {bgBusy ? 'Working…' : bgThumbUrl ? 'Replace image…' : 'Upload image…'}
          </Button>
          {bgThumbUrl && <Button variant="ghost" disabled={bgBusy} onClick={() => void removeBg()}>Remove</Button>}
        </div>
        {bgErr && <span className="sym-error">{bgErr}</span>}
        {bgThumbUrl && (
          <div className="sym-content-tint">
            <div className="label" style={{ margin: '10px 0 6px' }}>
              Overlay tint — the color wash over the image (thumbnail previews it; Save applies it)
            </div>
            <div className="sym-content-tint-row">
              <div className="sym-content-swatches" role="group" aria-label="Brand tint colors">
                {OVERLAY_SWATCHES.map((sw) => (
                  <button
                    key={sw.hex}
                    type="button"
                    className={d.bgOverlayColor === sw.hex ? 'sym-content-swatch sym-content-swatch--on' : 'sym-content-swatch'}
                    style={{ background: sw.hex }}
                    title={sw.name}
                    aria-label={sw.name}
                    aria-pressed={d.bgOverlayColor === sw.hex}
                    onClick={() => set('bgOverlayColor', sw.hex)}
                  />
                ))}
              </div>
              <label className="sym-content-tint-custom">
                Custom
                <input
                  type="color"
                  value={d.bgOverlayColor}
                  onChange={(e) => set('bgOverlayColor', e.target.value.toUpperCase())}
                />
              </label>
              <label className="sym-content-tint-opacity">
                Opacity
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={d.bgOverlayOpacity}
                  onChange={(e) => set('bgOverlayOpacity', Number(e.target.value))}
                />
                <span className="sym-content-tint-pct">{d.bgOverlayOpacity}%</span>
              </label>
            </div>
          </div>
        )}
      </div>
      <div className="sym-content-editor-foot">
        {err && <span className="sym-error">{err}</span>}
        {saved && !err && <span className="muted" style={{ fontSize: 12.5 }}>Saved — preview reloading with your changes.</span>}
        <Button variant="primary" size="sm" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save page content'}
        </Button>
      </div>
    </div>
  )
}

/** The invitation list names the household: one invitation covers both, and a
 *  spouse with no email of her own is otherwise invisible on every screen
 *  (Donna, 2026-08-26). Elsewhere in the app the record is still just him. */
function nameFor(contactById: Map<string, Contact>, id: string): string {
  const c = contactById.get(id)
  return c ? coupleName(c) : id
}

/** Sort key: most recent attended year first, then most recent invited. */
function historySort(c?: Contact): number {
  const att = Math.max(0, ...(c?.symposiumYears ?? []))
  const inv = Math.max(0, ...(c?.symposiumInvitedYears ?? []))
  return -(att * 10_000 + inv)
}

const HISTORY_START = 2022 // earliest year in Donna's workbook

/** Fixed year slots up to the year before the current event (max 5). */
function historyYears(eventYear?: number): number[] {
  const end = (Number(eventYear) || new Date().getFullYear()) - 1
  const years: number[] = []
  for (let y = HISTORY_START; y <= end; y++) years.push(y)
  return years.slice(-5)
}

/** Uniform year strip: solid = attended, outlined = invited only, dot = neither. */
function HistoryCell({ contact, years }: { contact?: Contact; years: number[] }) {
  const attended = new Set(contact?.symposiumYears ?? [])
  const invited = new Set(contact?.symposiumInvitedYears ?? [])
  if (!years.some((y) => attended.has(y) || invited.has(y))) return <span className="muted">—</span>
  return (
    <span className="sym-hist" title="Solid = attended · outlined = invited, did not attend">
      {years.map((y) => {
        const cls = attended.has(y) ? ' sym-hist-y--att' : invited.has(y) ? ' sym-hist-y--inv' : ' sym-hist-y--none'
        return (
          <span key={y} className={`sym-hist-y${cls}`}>
            {attended.has(y) || invited.has(y) ? `’${String(y).slice(2)}` : '·'}
          </span>
        )
      })}
    </span>
  )
}

/** Human string for a contact's shirt sizing ("L · Men's"), or null. */
function shirtLabel(c?: Contact): string | null {
  if (!c?.shirtSize && !c?.shirtStyle) return null
  return [c.shirtSize, c.shirtStyle === 'W' ? 'Women’s' : c.shirtStyle === 'M' ? 'Men’s' : undefined]
    .filter(Boolean).join(' · ')
}

/**
 * Invite WPI employees to attend — the bulk path.
 *
 * "Invite the employees" is not 215 dropdown selections, which is what the
 * one-at-a-time picker beside it would have required. Multi-select with
 * search, and a select-all that only ever covers what is actually on screen —
 * a select-all that silently included people you had filtered out would be a
 * mass mailing nobody reviewed.
 *
 * This only marks them as invited to attend. Nothing is emailed here: the
 * staff wave does the sending, so colleagues go through the same review,
 * dedupe and never-mail-twice rules as investors.
 */
function InviteStaffModal({ open, onClose, contacts, alreadyGuests, eventId, onDone }: {
  open: boolean; onClose: () => void; contacts: Contact[]
  alreadyGuests: Set<string>; eventId: string; onDone: () => Promise<void>
}) {
  const getToken = useApiToken()
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { if (open) { setQuery(''); setPicked(new Set()); setError(null) } }, [open])

  const staff = useMemo(() => {
    const q = query.trim().toLowerCase()
    return contacts
      .filter((c) => c.isWpi === true && !c.archived && !alreadyGuests.has(c.id))
      .filter((c) => !q || contactName(c).toLowerCase().includes(q) || (c.title ?? '').toLowerCase().includes(q))
      .sort((a, b) => contactName(a).localeCompare(contactName(b)))
  }, [contacts, query, alreadyGuests])

  const toggle = (id: string) => setPicked((p) => {
    const next = new Set(p)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const allShownPicked = staff.length > 0 && staff.every((c) => picked.has(c.id))

  const save = async () => {
    setBusy(true); setError(null)
    try {
      const token = await getToken()
      await api.symposium.addStaffInvites(token, eventId, [...picked])
      onClose(); await onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open} onClose={onClose} width={620}
      title="Invite WPI employees to attend"
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={busy || picked.size === 0} onClick={() => void save()}>
            {busy ? 'Adding…' : `Invite ${picked.size || ''}`.trim()}
          </Button>
        </>
      }
    >
      {error && <p className="sym-staff-error" role="alert">{error}</p>}
      <p className="sym-staff-note">
        Marks them as invited to attend. <b>Nothing is emailed here</b> — send a staff
        invitation wave from Waves when you are ready, which applies the same
        never-mail-twice and RSVP rules investors get.
      </p>
      <Field label="Find employees">
        <TextInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name or title…" />
      </Field>
      {staff.length === 0 ? (
        <EmptyState title="Nobody to add" hint={query ? 'No employee matches that search.' : 'Every WPI employee is already invited to attend.'} />
      ) : (
        <>
          <div className="sym-staff-tools">
            <span>{staff.length} shown{query ? ' (filtered)' : ''} · {picked.size} selected</span>
            <button
              type="button"
              className="sym-staff-all"
              onClick={() => setPicked((p) => {
                const next = new Set(p)
                // Only what is on screen — never the people a search hid.
                if (allShownPicked) staff.forEach((c) => next.delete(c.id))
                else staff.forEach((c) => next.add(c.id))
                return next
              })}
            >
              {allShownPicked ? 'Clear these' : 'Select all shown'}
            </button>
          </div>
          <div className="sym-staff-list">
            {staff.map((c) => (
              <label key={c.id} className="sym-staff-row">
                <input type="checkbox" checked={picked.has(c.id)} onChange={() => toggle(c.id)} />
                <span className="sym-staff-name">{contactName(c)}</span>
                <span className="sym-staff-title">{c.title ?? '—'}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </Modal>
  )
}

function AddInviteModal({ open, onClose, contacts, existing, eventId, onDone, staffOnly }: {
  open: boolean; onClose: () => void; contacts: Contact[]
  existing: Set<string>; eventId: string; onDone: () => Promise<void>; staffOnly?: boolean
}) {
  const getToken = useApiToken()
  const [contactId, setContactId] = useState('')
  const [tier, setTier] = useState('')
  useEffect(() => { if (open) { setContactId(''); setTier('') } }, [open])
  const candidates = contacts.filter((c) => !c.archived && !existing.has(c.id) && (staffOnly ? c.isWpi === true : !c.isWpi))
  const save = async () => {
    const token = await getToken()
    await api.symposium.addInvite(token, eventId, { contactId, tier: tier ? Number(tier) : null })
    onClose(); await onDone()
  }
  return (
    <Modal open={open} onClose={onClose} title={staffOnly ? 'Add WPI staff' : 'Add contact to invite list'} footer={
      <><Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!contactId} onClick={() => void save()}>Add</Button></>
    }>
      <Field label={staffOnly ? 'Team member' : 'Contact'} required hint={staffOnly ? 'WPI team members working the event — never part of the invitation list.' : 'From the CRM contact list — the Symposium never keeps a separate contact store.'}>
        <Select value={contactId} onChange={(e) => setContactId(e.target.value)}>
          <option value="">{staffOnly ? 'Choose a team member…' : 'Choose a contact…'}</option>
          {candidates.map((c) => <option key={c.id} value={c.id}>{contactName(c)}</option>)}
        </Select>
      </Field>
      {!staffOnly && (
        <Field label="Tier">
          <Select value={tier} onChange={(e) => setTier(e.target.value)}>
            <option value="">Unassigned</option><option value="1">Tier 1</option><option value="2">Tier 2</option><option value="3">Tier 3</option>
          </Select>
        </Field>
      )}
    </Modal>
  )
}

function WaveModal({ open, onClose, bundle, contactById, onDone }: {
  open: boolean; onClose: () => void; bundle: EventBundle
  contactById: Map<string, Contact>; onDone: () => Promise<void>
}) {
  const getToken = useApiToken()

  // ---- compose state -------------------------------------------------------
  const [name, setName] = useState('Save the Date')
  const [emailKind, setEmailKind] = useState<EmailKindId>('save_the_date')
  const [base, setBase] = useState<'invited' | 'tier1' | 'no_reply' | 'accepted' | 'staff'>('invited')
  const [exYes, setExYes] = useState(true)
  const [exNo, setExNo] = useState(true)
  const [exMaybe, setExMaybe] = useState(false)
  const [inclSent, setInclSent] = useState(false)
  // The keynote update thanks guests for their RSVP, so its audience is not
  // a choice: the people who said yes. Every one of them has been emailed
  // before, which the default "skip anyone already emailed" would drop.
  const pinnedToYes = emailKind === 'keynote_update'

  // ---- server plan (the sender's own resolver — numbers can't drift) -------
  const [plan, setPlan] = useState<Awaited<ReturnType<typeof api.symposium.wavePlanPreview>> | null>(null)
  const [planErr, setPlanErr] = useState<string | null>(null)
  const [step, setStep] = useState<'compose' | 'roster'>('compose')
  const [unticked, setUnticked] = useState<Set<string>>(new Set())
  const [rosterFilter, setRosterFilter] = useState('')
  const [busy, setBusy] = useState(false)
  const [sending, setSending] = useState<null | {
    waveId: string; toSend: number; sent: number; failed: number; done: boolean
    status?: string; lastError?: string | null
  }>(null)
  const [result, setResult] = useState<string | null>(null)

  const filterBody = useMemo(() => ({
    // An "accepted" audience IS the yes-sayers — the skip-Yes default would
    // empty it, so that exclusion is forced off for this base.
    base: pinnedToYes ? 'accepted' as const : base,
    excludeRsvpYes: pinnedToYes || base === 'accepted' ? false : exYes,
    excludeRsvpNo: exNo, excludeRsvpMaybe: exMaybe,
    includeAlreadySent: pinnedToYes ? true : inclSent, excludeInviteIds: [] as string[],
  }), [base, exYes, exNo, exMaybe, inclSent, pinnedToYes])

  useEffect(() => {
    if (open) {
      setName('Save the Date'); setEmailKind('save_the_date'); setBase('invited')
      setExYes(true); setExNo(true); setExMaybe(false); setInclSent(false)
      setStep('compose'); setUnticked(new Set()); setRosterFilter('')
      setResult(null); setSending(null); setPlan(null)
    }
  }, [open])

  // Debounced server plan on every filter change.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    const t = setTimeout(() => {
      void (async () => {
        try {
          const token = await getToken()
          const r = await api.symposium.wavePlanPreview(token, bundle.event.id, filterBody)
          if (!cancelled) { setPlan(r); setPlanErr(null); setUnticked(new Set()) }
        } catch (e) {
          if (!cancelled) setPlanErr(e instanceof Error ? e.message : String(e))
        }
      })()
    }, 350)
    return () => { cancelled = true; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filterBody, bundle.event.id])

  const audience = plan?.audience ?? []
  const sendCount = audience.filter((a: any) => a.excluded === null && !unticked.has(a.id)).length

  // Every way of choosing the template goes through here, so the keynote
  // update's audience is pinned whether it was picked by wave name or from the
  // template list — the list alone used to leave "Everyone invited", which
  // would have thanked 177 non-responders for an RSVP they never sent
  // (2026-09-29 review). Leaving it restores the modal's defaults.
  const chooseKind = (k: EmailKindId) => {
    if (k === 'keynote_update') { setBase('accepted'); setInclSent(true) }
    else if (emailKind === 'keynote_update') { setBase('invited'); setInclSent(false) }
    setEmailKind(k)
  }

  // A wave name preset implies a sensible template; both stay changeable.
  const pickName = (n: string) => {
    setName(n)
    if (n === 'Save the Date') chooseKind('save_the_date')
    // The keynote invitation is news for everyone who has not answered —
    // including the people already emailed a save-the-date or invitation.
    // Left at "skip anyone already emailed", it plans 2 sends instead of 177.
    else if (n === 'Keynote announced') { chooseKind('invitation'); setInclSent(true) }
    else if (n === 'Keynote update') chooseKind('keynote_update')
    else if (n === 'Reminder') chooseKind('reminder')
    else if (n === 'Final call') chooseKind('final_call')
  }

  // Delivery runs SERVER-SIDE and outlives this request (~25 msg/min under
  // Exchange's throttle). The POST returns 202; progress is polled off the
  // wave row, and closing the modal does not stop the run.
  const draftAndSend = async () => {
    setBusy(true)
    try {
      const token = await getToken()
      const wave = await api.symposium.draftWave(token, bundle.event.id, {
        name,
        emailKind,
        audienceFilter: { ...filterBody, excludeInviteIds: [...unticked] },
      })
      if (!bundle.emailEnabled) {
        setResult('Draft saved with its audience recorded — nothing is delivered while sending is disabled.')
        await onDone()
        return
      }
      const started = await api.symposium.sendWave(token, wave.id)
      setSending({ waveId: wave.id, toSend: started.toSend, sent: 0, failed: 0, done: false })
      void pollWave(wave.id)
    } catch (e) {
      setResult(e instanceof Error ? e.message : String(e))
      await onDone()
    } finally { setBusy(false) }
  }

  const pollWave = async (waveId: string) => {
    for (let i = 0; i < 400; i += 1) {
      await new Promise((r) => setTimeout(r, 3000))
      try {
        const token = await getToken()
        const b = await api.symposium.getEvent(token, bundle.event.id)
        const w = (b.waves ?? []).find((x: any) => String(x.id).toLowerCase() === String(waveId).toLowerCase())
        if (!w) continue
        const done = w.status === 'sent' || w.status === 'failed'
        setSending({
          waveId, toSend: w.targetCount ?? 0, sent: w.sentCount ?? 0,
          failed: w.failedCount ?? 0, done, status: w.status, lastError: w.lastError ?? null,
        })
        if (done) {
          setResult(w.status === 'sent'
            ? `Finished — ${num(w.sentCount ?? 0)} sent${w.failedCount ? `, ${num(w.failedCount)} failed` : ''}.`
            : `Stopped — ${num(w.sentCount ?? 0)} sent, ${num(w.failedCount ?? 0)} failed. ${w.lastError ?? ''}`)
          await onDone()
          return
        }
      } catch { /* transient — keep polling */ }
    }
  }

  const exclusionSummary = plan ? [
    plan.excludedRsvp > 0 ? `${num(plan.excludedRsvp)} already answered (excluded)` : null,
    plan.alreadySent > 0 ? `${num(plan.alreadySent)} already emailed (no duplicates)` : null,
    plan.noEmail > 0 ? `${num(plan.noEmail)} missing an email address` : null,
    unticked.size > 0 ? `${num(unticked.size)} unticked by you` : null,
    plan.resends > 0 ? `${num(plan.resends)} will receive a SECOND email (re-send is on)` : null,
    plan.correctedAddress > 0
      ? `${num(plan.correctedAddress)} included because their address was corrected after the last send — they never received that copy`
      : null,
  ].filter(Boolean) as string[] : []

  const rosterRows = rosterFilter
    ? audience.filter((a: any) => nameFor(contactById, a.contactId).toLowerCase().includes(rosterFilter.toLowerCase()))
    : audience

  const EXCLUDED_LABEL: Record<string, string> = {
    rsvp: 'already answered', already_sent: 'already emailed', no_email: 'no email', manual: 'unticked',
  }

  return (
    <Modal open={open} onClose={onClose} title="Send invitation wave" footer={
      <>
        <Button variant="default" onClick={onClose}>Close</Button>
        {!result && !sending && step === 'compose' && (
          <Button variant="primary" disabled={!plan || plan.toSend === 0} onClick={() => setStep('roster')}>
            Review recipients…
          </Button>
        )}
        {!result && !sending && step === 'roster' && (
          <>
            <Button variant="default" onClick={() => setStep('compose')}>Back</Button>
            <Button variant="primary" disabled={busy || sendCount === 0} onClick={() => void draftAndSend()}>
              {bundle.emailEnabled
                ? `Yes — send to ${num(sendCount)} ${sendCount === 1 ? 'person' : 'people'}`
                : 'Save draft (sending disabled)'}
            </Button>
          </>
        )}
      </>
    }>
      {!bundle.emailEnabled && (
        <div className="sym-wave-notice">
          <ShieldAlert size={15} />
          <span>
            Outbound Symposium email is <b>disabled (kill switch)</b>. The wave will be saved as a
            draft with its audience recorded; nothing is delivered until sending is explicitly enabled.
          </span>
        </div>
      )}

      {sending && (
        <div style={{ marginBottom: 14 }}>
          <p style={{ fontSize: 14, margin: '0 0 8px' }}>
            {sending.done
              ? <b>Finished.</b>
              : <><b>Sending…</b> {num(sending.sent)} of {num(sending.toSend)} — about{' '}
                  {Math.max(1, Math.ceil(((sending.toSend - sending.sent) * 2.4) / 60))} min left.</>}
          </p>
          <div style={{ height: 8, borderRadius: 4, background: 'var(--wpi-fog, #E2E8ED)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 4,
              width: `${sending.toSend ? Math.round((sending.sent / sending.toSend) * 100) : 0}%`,
              background: sending.status === 'failed' ? 'var(--alert, #C73E3E)' : 'var(--ok, #1D9E75)',
              transition: 'width .4s ease',
            }} />
          </div>
          <p className="dim" style={{ fontSize: 12, margin: '6px 0 0' }}>
            {sending.failed > 0 && <><b>{num(sending.failed)} failed.</b>{' '}</>}
            Sending continues on the server — you can close this window and it will keep going.
          </p>
          {sending.lastError && (
            <p style={{ fontSize: 12, margin: '4px 0 0', color: 'var(--alert, #a33)' }}>{sending.lastError}</p>
          )}
        </div>
      )}

      {!sending && !result && step === 'compose' && (
        <>
          <div className="sym-content-grid">
            <Field label="Wave name">
              <Select value={name} onChange={(e) => pickName(e.target.value)}>
                {['Save the Date', 'Keynote announced', 'Keynote update', 'Reminder', 'Final call'].map((n) => <option key={n}>{n}</option>)}
              </Select>
            </Field>
            <Field label="Email template (see the Email Preview tab)">
              <Select value={emailKind} onChange={(e) => chooseKind(e.target.value as EmailKindId)}>
                {EMAIL_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </Select>
            </Field>
          </div>
          <Field
            label="Audience"
            hint={pinnedToYes
              ? 'The keynote update thanks guests for their RSVP, so it only goes to people who said yes.'
              : base === 'staff'
                ? 'WPI employees ticked “Invited to attend”. Investors are not in this wave.'
                : undefined}
          >
            <Select value={pinnedToYes ? 'accepted' : base} disabled={pinnedToYes} onChange={(e) => setBase(e.target.value as never)}>
              <option value="invited">Everyone invited</option>
              <option value="tier1">Tier 1 only</option>
              <option value="no_reply">Invited · no reply yet</option>
              <option value="accepted">Accepted (RSVP yes)</option>
              {/* Its own audience, never a checkbox on the others — a staff
                  wave and an investor wave must not be one send. */}
              <option value="staff">WPI employees invited to attend</option>
            </Select>
          </Field>
          {base === 'staff' && (
            <p className="sym-staff-note">
              This sends to colleagues only. They receive the same invitation investors get,
              and because they are on WPI domains a staff wave is the one audience that can be
              genuinely test-sent outside production.
            </p>
          )}
          <div style={{ display: 'grid', gap: 6, margin: '2px 0 12px', fontSize: 13.5 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={pinnedToYes || base === 'accepted' ? false : exYes} onChange={(e) => setExYes(e.target.checked)} disabled={pinnedToYes || base === 'accepted'} />
              Skip everyone who already said <b>Yes</b>
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={exNo} onChange={(e) => setExNo(e.target.checked)} />
              Skip everyone who said <b>No / can&rsquo;t attend</b>
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={exMaybe} onChange={(e) => setExMaybe(e.target.checked)} />
              Skip the <b>Maybes</b>
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={pinnedToYes || inclSent} onChange={(e) => setInclSent(e.target.checked)} disabled={pinnedToYes} />
              <span><b>Include people we already emailed</b> — tick this for a second save-the-date, a reminder or a keynote update</span>
            </label>
          </div>
          {planErr && <p style={{ fontSize: 12.5, color: 'var(--alert, #a33)' }}>Couldn&rsquo;t compute the audience: {planErr}</p>}
          {plan && (
            <>
              <p style={{ fontSize: 13, margin: '0 0 4px' }}>
                Will send to <b>{num(plan.toSend)}</b> of {num(plan.total)} in this audience.
              </p>
              {exclusionSummary.length > 0 && (
                <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.6, margin: 0 }}>
                  {exclusionSummary.join(' · ')}.
                </p>
              )}
            </>
          )}
        </>
      )}

      {!sending && !result && step === 'roster' && plan && (
        <>
          <p style={{ fontSize: 14, margin: '2px 0 8px' }}>
            <b>&ldquo;{name}&rdquo;</b> ({EMAIL_KINDS.find((k) => k.id === emailKind)?.label} email) will go to{' '}
            <b>{num(sendCount)} {sendCount === 1 ? 'person' : 'people'}</b>. Untick anyone who should not get it.
          </p>
          {exclusionSummary.length > 0 && (
            <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.6, margin: '0 0 8px' }}>
              {exclusionSummary.join(' · ')}.
            </p>
          )}
          <div className="toolbar-search" style={{ maxWidth: 260, marginBottom: 8 }}>
            <Search size={15} />
            <input placeholder="Find a name…" value={rosterFilter} onChange={(e) => setRosterFilter(e.target.value)} />
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--wpi-fog, #E2E8ED)', borderRadius: 8, padding: '6px 10px' }}>
            {rosterRows.map((a: any) => {
              const excludedByFilter = a.excluded !== null
              const checked = !excludedByFilter && !unticked.has(a.id)
              return (
                <label key={a.id} style={{
                  display: 'flex', gap: 8, alignItems: 'center', padding: '4px 2px', fontSize: 13,
                  opacity: excludedByFilter ? 0.5 : 1,
                }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={excludedByFilter}
                    onChange={(e) => setUnticked((u) => {
                      const next = new Set(u)
                      if (e.target.checked) next.delete(a.id); else next.add(a.id)
                      return next
                    })}
                  />
                  <span style={{ flex: 1 }}>{nameFor(contactById, a.contactId)}</span>
                  {a.rsvp !== 'none' && <Badge tone={RSVP_TONE[a.rsvp] ?? 'neutral'} variant="soft">{a.rsvp}</Badge>}
                  {a.addressChanged
                    ? (
                      <span title={`Last mailed to ${a.mailedTo} — the address has been corrected since`}>
                        <Badge tone="warn" variant="outline">address corrected</Badge>
                      </span>
                    )
                    : a.alreadySent && <Badge tone="neutral" variant="outline">emailed before</Badge>}
                  {a.bounced && <Badge tone="alert" variant="soft">bounced</Badge>}
                  {excludedByFilter && <span className="dim" style={{ fontSize: 12 }}>{EXCLUDED_LABEL[a.excluded] ?? a.excluded}</span>}
                </label>
              )
            })}
            {rosterRows.length === 0 && <p className="dim" style={{ fontSize: 12.5, margin: '6px 2px' }}>No one matches.</p>}
          </div>
        </>
      )}

      {result && <p style={{ fontSize: 13, color: 'var(--wpi-slate)' }}><b>Result:</b> {result}</p>}
    </Modal>
  )
}

/* ============================ Single-invite email ============================ */

function EmailInviteModal({ invite, contact, bundle, onClose, refresh }: {
  invite: any; contact: Contact | undefined; bundle: EventBundle
  onClose: () => void; refresh: () => Promise<void>
}) {
  const getToken = useApiToken()
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmReal, setConfirmReal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [kind, setKind] = useState<EmailKindId>('save_the_date')
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewSubject, setPreviewSubject] = useState<string | null>(null)
  const [previewErr, setPreviewErr] = useState<string | null>(null)
  const name = contact ? contactName(contact) : invite.contactId
  const email = contact?.workEmail || contact?.personalEmail || null
  const ev = bundle.event
  // The keynote update says "Thank you for your RSVP" — the server refuses a
  // real send to anyone who has not said yes, so offer the right email here
  // instead of a button that fails. A test to yourself is still a preview.
  const yesOnlyBlocked = kind === 'keynote_update' && invite.rsvp !== 'yes'

  const send = async (mode: 'test' | 'real') => {
    setBusy(true); setError(null); setResult(null)
    try {
      const token = await getToken()
      const r = await api.symposium.sendInvite(token, invite.id, mode, kind)
      if (r.stampFailed) {
        // The message went; only its record failed. Said plainly so nobody
        // reads "not mailed" off the invite and sends it again.
        setError(`Invitation sent to ${r.to}, but recording it failed (send ${r.sendId ?? '?'}) — the invite will still read as not mailed. Do not resend; the audit log has the details.`)
      } else {
        setResult(mode === 'test' ? `Test email sent to ${r.to} — check your inbox.` : `Invitation sent to ${r.to}.`)
      }
      setConfirmReal(false)
      if (mode === 'real') await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setConfirmReal(false)
    } finally { setBusy(false) }
  }

  const copyLink = async () => {
    const token = await getToken()
    const r = await api.symposium.trackingLink(token, invite.id)
    // Trusted portal-domain URL (server-built); path fallback for an old API.
    await navigator.clipboard.writeText(r.url ?? `${API_BASE.replace(/\/api$/, '')}${r.path}`)
    setCopied(true); setTimeout(() => setCopied(false), 2000)
  }

  // The REAL rendered invitation, built by the same code the send path uses —
  // so what's on screen is what goes out. Loaded on open; nothing is mailed.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const token = await getToken()
        const r = await api.symposium.previewEmail(token, invite.id, kind)
        if (!cancelled) { setPreviewHtml(r.html); setPreviewSubject(r.subject); setPreviewErr(null) }
      } catch (e) {
        if (!cancelled) setPreviewErr(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite.id, kind])

  return (
    <Modal open onClose={onClose} title={`Invitation email — ${name}`} footer={
      <>
        <Button variant="default" onClick={onClose}>Close</Button>
        <Button variant="default" disabled={busy || !bundle.emailEnabled} onClick={() => void send('test')}>
          Send test to me
        </Button>
        {confirmReal
          ? <Button variant="primary" disabled={busy || !bundle.emailEnabled || !email || yesOnlyBlocked} onClick={() => void send('real')}>
              Confirm — send to {email}
            </Button>
          : <Button variant="primary" disabled={busy || !bundle.emailEnabled || !email || yesOnlyBlocked} onClick={() => setConfirmReal(true)}>
              Send invitation…
            </Button>}
      </>
    }>
      {!bundle.emailEnabled && (
        <div className="sym-wave-notice">
          <ShieldAlert size={15} />
          <span>Outbound Symposium email is <b>disabled (kill switch)</b> — both buttons below will be refused by the server until it is enabled.</span>
        </div>
      )}
      <div className="sym-preview-controls" style={{ marginBottom: 10 }}>
        {EMAIL_KINDS.map((k) => (
          <button key={k.id} className={`chip${kind === k.id ? ' active' : ''}`} onClick={() => { setKind(k.id); setConfirmReal(false) }}>{k.label}</button>
        ))}
      </div>
      {yesOnlyBlocked && (
        <div className="sym-wave-notice">
          <ShieldAlert size={15} />
          <span>
            The keynote update thanks guests for their RSVP, and {name} hasn&rsquo;t said yes.{' '}
            <button className="chip" onClick={() => { setKind('invitation'); setConfirmReal(false) }}>Send the Invitation instead</button>
          </span>
        </div>
      )}
      <p style={{ fontSize: 13.5, margin: '0 0 10px' }}>
        <b>To:</b>{' '}
        {email ?? <Badge tone="warn" variant="soft">no email on the contact record — fix it there first</Badge>}
        <br />
        <b>Subject:</b> {previewSubject ?? `${ev.title}`}
      </p>
      <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.6, margin: '0 0 10px' }}>
        Below is the <b>actual email</b>, rendered by the same code that sends it — personalised
        to this invitee, with their own tracked RSVP link. <b>Send test to me</b> delivers it to
        your inbox (subject prefixed [TEST], invitee untouched). <b>Send invitation</b> emails the
        invitee and marks them Invited.
      </p>

      <div style={{
        border: '1px solid var(--wpi-fog, #E2E8ED)', borderRadius: 8, overflow: 'hidden',
        marginBottom: 12, background: '#2F4B69',
      }}>
        {previewErr ? (
          <p style={{ padding: 14, margin: 0, fontSize: 12.5, color: 'var(--alert, #a33)' }}>
            Couldn&rsquo;t render the preview: {previewErr}
          </p>
        ) : previewHtml === null ? (
          <p style={{ padding: 14, margin: 0, fontSize: 12.5, color: '#EAF1F9' }}>Rendering the invitation…</p>
        ) : (
          <iframe
            title="Invitation preview"
            srcDoc={previewHtml}
            sandbox=""
            style={{ display: 'block', width: '100%', height: 460, border: 0, background: '#2F4B69' }}
          />
        )}
      </div>
      <button className="sym-mini sym-mini--wide" onClick={() => void copyLink()}>
        {copied ? 'Copied ✓' : 'Copy tracked link'}
      </button>
      {result && <p style={{ fontSize: 13, marginTop: 12, color: 'var(--ok, #1c6b38)' }}><b>{result}</b></p>}
      {error && <p style={{ fontSize: 13, marginTop: 12, color: 'var(--alert, #a33)' }}><b>Send failed:</b> {error}</p>}
    </Modal>
  )
}

/* ============================ Email preview + editor ============================ */

/* The email twin of the Portal Preview tab (Chauncey/Jason 2026-08-24):
   pick one of the four templates, see the ACTUAL email rendered by the same
   code that sends it, edit the words, send yourself a test. Copy lives in
   crm.symposium_events.email_content (migration 023); empty field = the
   built-in default (shown as the placeholder), so a partial edit never
   blanks the rest of the email. */

type EmailCopyDraft = {
  subject: string; headline: string; greetingNote: string
  introBanner: string; buttonLabel: string; buttonNote: string
}
const EMPTY_COPY: EmailCopyDraft = {
  subject: '', headline: '', greetingNote: '', introBanner: '', buttonLabel: '', buttonNote: '',
}

function EmailPreviewTab({ bundle, refresh }: { bundle: EventBundle; refresh: () => Promise<void> }) {
  const getToken = useApiToken()
  const ev = bundle.event
  const [kind, setKind] = useState<EmailKindId>('save_the_date')
  const [preview, setPreview] = useState<{ subject: string; html: string; defaults: EmailCopyDraft } | null>(null)
  const [previewErr, setPreviewErr] = useState<string | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [drafts, setDrafts] = useState<Record<EmailKindId, EmailCopyDraft>>(() => {
    const stored = (ev.emailContent ?? {}) as Record<string, Partial<EmailCopyDraft>>
    const init = {} as Record<EmailKindId, EmailCopyDraft>
    for (const k of EMAIL_KINDS) init[k.id] = { ...EMPTY_COPY, ...(stored[k.id] ?? {}) }
    return init
  })
  const [saving, setSaving] = useState(false)
  const [testBusy, setTestBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const loadPreview = useCallback(async (k: EmailKindId) => {
    setPreviewErr(null)
    try {
      const token = await getToken()
      const r = await api.symposium.previewEventEmail(token, ev.id, k)
      setPreview(r as never)
    } catch (e) {
      setPreviewErr(e instanceof Error ? e.message : String(e))
    }
  }, [getToken, ev.id])

  useEffect(() => { void loadPreview(kind) }, [kind, loadPreview])

  const setField = (field: keyof EmailCopyDraft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setDrafts((d) => ({ ...d, [kind]: { ...d[kind], [field]: e.target.value } }))

  const save = async () => {
    setSaving(true); setNotice(null)
    try {
      // All four kinds at once — empty fields (and kinds that clean to
      // nothing) are omitted, so the server stores only real overrides.
      const content: Record<string, Partial<EmailCopyDraft>> = {}
      for (const k of EMAIL_KINDS) {
        const entry: Partial<EmailCopyDraft> = {}
        for (const [field, value] of Object.entries(drafts[k.id])) {
          if (value.trim()) entry[field as keyof EmailCopyDraft] = value.trim()
        }
        if (Object.keys(entry).length) content[k.id] = entry
      }
      const token = await getToken()
      await api.symposium.patchEvent(token, ev.id, {
        emailContent: Object.keys(content).length ? content : null,
      })
      await refresh()
      await loadPreview(kind)
      setNotice('Saved — the preview below is what the next send will use.')
    } catch (e) {
      setNotice(`Save failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally { setSaving(false) }
  }

  const sendTest = async () => {
    setTestBusy(true); setNotice(null)
    try {
      const token = await getToken()
      const r = await api.symposium.sendTestEventEmail(token, ev.id, kind)
      setNotice(`Test email sent to ${r.to} — check your inbox.`)
    } catch (e) {
      setNotice(`Test send failed: ${e instanceof Error ? e.message : String(e)}`)
    } finally { setTestBusy(false) }
  }

  const d = drafts[kind]
  const ph = preview?.defaults ?? EMPTY_COPY

  return (
    <SectionCard
      eyebrow="Exactly what lands in their inbox"
      title={<>Invitation email <b>Preview</b></>}
      right={
        <div className="sym-preview-controls">
          <Button variant={editOpen ? 'primary' : 'default'} size="sm" icon={<Pencil size={14} />} onClick={() => setEditOpen((o) => !o)}>
            {editOpen ? 'Close editor' : 'Edit email content'}
          </Button>
          <Button variant="default" size="sm" icon={<Send size={14} />} disabled={testBusy || !bundle.emailEnabled} onClick={() => void sendTest()}>
            Send test to me
          </Button>
        </div>
      }
    >
      {!bundle.emailEnabled && (
        <div className="sym-wave-notice">
          <ShieldAlert size={15} />
          <span>Outbound Symposium email is <b>disabled (kill switch)</b> — previews and editing work; test sends are refused by the server.</span>
        </div>
      )}

      <div className="sym-preview-controls" style={{ marginBottom: 12 }}>
        {EMAIL_KINDS.map((k) => (
          <button key={k.id} className={`chip${kind === k.id ? ' active' : ''}`} onClick={() => setKind(k.id)}>{k.label}</button>
        ))}
        {preview && <span className="muted" style={{ fontSize: 12.5 }}><b>Subject:</b> {preview.subject}</span>}
      </div>

      {editOpen && (
        <div className="sym-content-editor">
          <p className="dim" style={{ fontSize: 12.5, margin: '0 0 10px' }}>
            Editing the <b>{EMAIL_KINDS.find((k) => k.id === kind)?.label}</b> email. Empty field = the
            built-in wording (shown greyed). Words only — the artwork layout never changes.
          </p>
          <div className="sym-content-grid">
            <Field label="Subject line"><TextInput value={d.subject} onChange={setField('subject')} placeholder={ph.subject} /></Field>
            <Field label="Headline (the big title line)"><TextInput value={d.headline} onChange={setField('headline')} placeholder={ph.headline} /></Field>
          </div>
          <Field label="Note under the greeting (reminder wording — empty on the save-the-date)">
            <TextArea rows={2} value={d.greetingNote} onChange={setField('greetingNote')} placeholder={ph.greetingNote || 'None — add a short personal line here'} />
          </Field>
          <Field label="Blue banner paragraph">
            <TextArea rows={2} value={d.introBanner} onChange={setField('introBanner')} placeholder={ph.introBanner} />
          </Field>
          <div className="sym-content-grid">
            <Field label="Button"><TextInput value={d.buttonLabel} onChange={setField('buttonLabel')} placeholder={ph.buttonLabel} /></Field>
            <Field label="Line under the button"><TextInput value={d.buttonNote} onChange={setField('buttonNote')} placeholder={ph.buttonNote} /></Field>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <Button variant="primary" size="sm" disabled={saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save email content'}</Button>
            <span className="dim" style={{ fontSize: 12 }}>Applies to every send of this template — single invitations and waves.</span>
          </div>
        </div>
      )}

      {notice && <p style={{ fontSize: 13, margin: '0 0 10px' }}><b>{notice}</b></p>}

      <div style={{
        border: '1px solid var(--wpi-fog, #E2E8ED)', borderRadius: 8, overflow: 'hidden', background: '#2F4B69',
      }}>
        {previewErr ? (
          <p style={{ padding: 14, margin: 0, fontSize: 12.5, color: 'var(--alert, #a33)' }}>Couldn&rsquo;t render the preview: {previewErr}</p>
        ) : preview === null ? (
          <p style={{ padding: 14, margin: 0, fontSize: 12.5, color: '#EAF1F9' }}>Rendering…</p>
        ) : (
          <iframe title="Email preview" srcDoc={preview.html} sandbox="" style={{ display: 'block', width: '100%', height: 680, border: 0, background: '#2F4B69' }} />
        )}
      </div>
      <p className="dim" style={{ fontSize: 12, margin: '8px 0 0' }}>
        Rendered by the same code that sends — generic greeting (&ldquo;Dear Friend&rdquo;); real sends personalise
        it and carry each invitee&rsquo;s own tracked RSVP link.
      </p>
    </SectionCard>
  )
}

/* ============================ RSVP ============================ */

function RsvpTab({ bundle, invites, contactById, accountById, refresh }: {
  bundle: EventBundle; invites: any[]; contactById: Map<string, Contact>
  accountById: Map<string, Account>; refresh: () => Promise<void>
}) {
  const getToken = useApiToken()
  const { kpis } = bundle
  const histYears = historyYears(bundle.event.year)

  // Funnel steps drill: clicking one filters the roster below to those people.
  const [funnelSel, setFunnelSel] = useState<string | null>(null)
  const roster = useMemo(() => {
    switch (funnelSel) {
      case 'invited': return invites.filter((i) => i.inviteStatus === 'invited')
      case 'clicked': return invites.filter((i) => i.firstHumanClickedAt)
      case 'visited': return invites.filter((i) => i.firstHumanVisitedAt)
      case 'yes': case 'maybe': case 'no': return invites.filter((i) => i.rsvp === funnelSel)
      case 'none': return invites.filter((i) => i.inviteStatus === 'invited' && i.rsvp === 'none')
      default: return invites.filter((i) => i.rsvp !== 'none')
    }
  }, [invites, funnelSel])

  const toggleCheckin = async (i: any) => {
    const token = await getToken()
    await api.symposium.patchInvite(token, i.id, { checkedIn: !i.checkedIn })
    await refresh()
  }
  const patchRoster = async (inviteId: string, body: object) => {
    const token = await getToken()
    try {
      await api.symposium.patchInvite(token, inviteId, body)
    } catch (err) {
      alert(`Not saved: ${err instanceof Error ? err.message : String(err)}`)
    }
    await refresh()
  }

  const exportCsv = () => downloadCsv(
    `symposium-${bundle.event.year}-checkin.csv`,
    ['Contact', 'Investor Group', 'Party', 'RSVP', 'Gift', 'Shirt size', 'Gift delivery', 'Checked in'],
    roster.map((i) => [
      nameFor(contactById, i.contactId),
      companyFor(contactById, accountById, i) ?? '',
      i.partySize ?? 1, i.rsvp,
      i.gift ? 'yes' : 'no',
      i.gift ? shirtLabel(contactById.get(i.contactId)) ?? '' : '',
      i.gift ? giftDeliveryLabel(i.giftDelivery) : '',
      i.checkedIn ? 'yes' : 'no',
    ]),
  )

  const cols: Column<any>[] = [
    {
      key: 'contact', header: 'Contact', sortValue: (i) => nameFor(contactById, i.contactId),
      render: (i) => contactById.has(i.contactId)
        ? <Link className="sym-contact sym-contact--link" to={`/contacts/${i.contactId}`} title="Open the CRM contact record">{nameFor(contactById, i.contactId)}</Link>
        : nameFor(contactById, i.contactId),
    },
    {
      key: 'group', header: 'Investor Group',
      sortValue: (i) => companyFor(contactById, accountById, i) ?? '',
      render: (i) => <span className="muted">{companyFor(contactById, accountById, i) ?? '—'}</span>,
    },
    {
      key: 'history', header: 'History', width: 142, align: 'center',
      sortValue: (i) => historySort(contactById.get(i.contactId)),
      render: (i) => <HistoryCell contact={contactById.get(i.contactId)} years={histYears} />,
    },
    {
      key: 'engagement', header: 'Engagement', width: 120, align: 'center',
      sortValue: (i) => (i.firstHumanVisitedAt ? 2 : i.firstHumanClickedAt ? 1 : i.deliveryStatus === 'bounced' ? -1 : 0),
      // Bounce-only by design (Chauncey 2026-07-30): silence means fine —
      // no positive "delivered" inference is shown.
      //
      // Badges show HUMAN-graded hits only (migration 017). A scanner-only
      // row renders as the same quiet dash as silence, because a robot
      // fetching the link is not the investor showing interest — but the
      // tooltip keeps the fact discoverable for anyone chasing a no-reply.
      render: (i) => i.firstHumanVisitedAt
        ? <Badge tone="ok" variant="soft" dot>{`visited ${fmtDate(i.firstHumanVisitedAt, 'short')}`}</Badge>
        : i.firstHumanClickedAt
          ? <Badge tone="warn" variant="soft" dot>{`opened ${fmtDate(i.firstHumanClickedAt, 'short')}`}</Badge>
          : i.deliveryStatus === 'bounced'
            ? <Badge tone="alert" variant="soft" dot>bounced</Badge>
            : i.firstClickedAt
              ? <span className="dim" title={`Their mail system fetched the link ${fmtDate(i.firstClickedAt, 'short')} — a security scanner, not a person. It does confirm the invitation reached them.`}>—</span>
              : <span className="dim">—</span>,
    },
    { key: 'party', header: 'Party', width: 70, align: 'center', sortValue: (i) => i.partySize ?? 1, render: (i) => i.partySize ?? 1 },
    { key: 'rsvp', header: 'RSVP', width: 130, align: 'center', sortValue: (i) => i.rsvp, render: (i) => <RsvpSelect invite={i} onPatch={(b) => void patchRoster(i.id, b)} /> },
    {
      // Replaces the never-used "Gift status" (migration 008). At check-in,
      // "Picked up at Symposium" is the handover — and the Yes/No sits right
      // above it, so a guest whose Gift is still No can be handed one at the
      // door without leaving check-in for the Invitation List.
      key: 'gift', header: 'Gift / delivery', width: 180, className: 'sym-gift-td', sortValue: giftSortValue,
      render: (i) => <GiftCell invite={i} onPatch={(b) => void patchRoster(i.id, b)} />,
    },
    {
      key: 'shirt', header: 'Shirt size', width: 110, align: 'center',
      sortValue: (i) => (i.gift ? shirtLabel(contactById.get(i.contactId)) ?? '' : 'zz'),
      render: (i) => (!i.gift
        ? <Badge tone="neutral" variant="outline">no gift</Badge>
        : <span className="muted">{shirtLabel(contactById.get(i.contactId)) ?? '—'}</span>),
    },
    {
      key: 'checkin', header: 'Checked in', width: 110, align: 'center', sortValue: (i) => (i.checkedIn ? 0 : 1),
      render: (i) => (
        <button className={`sym-checkin${i.checkedIn ? ' on' : ''}`} onClick={() => void toggleCheckin(i)}>
          {i.checkedIn ? '✓ In' : 'Check in'}
        </button>
      ),
    },
  ]

  return (
    <div className="sym-stack">
      <SectionCard eyebrow="Live funnel · click a step to see who" title={<>RSVP <b>Funnel</b></>}>
        <div className="sym-funnel">
          {([
            ['Invited', kpis.invited, 'invited'],
            // Clicked/Visited are HUMAN-GRADED (migration 017). Mail scanners
            // fetch and run the JS on every tracked link, so the raw counts
            // measured robots — wave 1 read 44/36 and almost none were people.
            ['Opened', kpis.clicked ?? 0, 'clicked'], ['Visited portal', kpis.visited ?? 0, 'visited'],
            ['Yes', kpis.rsvpYes, 'yes'], ['Maybe', kpis.rsvpMaybe, 'maybe'],
            ['Declined', kpis.declined, 'no'], ['No reply', kpis.pending, 'none'],
          ] as Array<[string, number, string]>).map(([l, v, sel]) => (
            <button
              key={l}
              className={`sym-funnel-step${funnelSel === sel ? ' active' : ''}`}
              onClick={() => setFunnelSel(funnelSel === sel ? null : sel)}
              title={`Show these ${num(v)} in the roster below`}
            >
              <span className="sym-funnel-v num">{num(v)}</span>
              <span className="sym-funnel-l label">{l}</span>
            </button>
          ))}
        </div>
        <p className="sym-headcount-note">
          {num(kpis.rsvpYesSeats)} people coming ({num(kpis.rsvpYes)} RSVPs + {num(kpis.rsvpYesGuests)} guests)
          {' '}· {num(kpis.staffWorking)} WPI staff working
          {kpis.staffOtherAttending > 0 && <> · {num(kpis.staffOtherAttending)} other WPI attending</>}
          {' '}· <b>{num(kpis.totalOnSite)} total on site</b>
        </p>
      </SectionCard>
      <SectionCard
        eyebrow={funnelSel ? 'Filtered by the funnel step above' : 'Day-of ready'}
        title={<>{funnelSel ? ({ invited: 'Invited', clicked: 'Clicked the Link', visited: 'Visited the Portal', yes: 'RSVP Yes', maybe: 'RSVP Maybe', no: 'Declined', none: 'No Reply' } as Record<string, string>)[funnelSel] : 'Attendee'} <b>Roster</b></>}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {funnelSel && <Button variant="default" size="sm" onClick={() => setFunnelSel(null)}>Show responded</Button>}
            <Button variant="default" size="sm" icon={<Download size={14} />} onClick={exportCsv}>Export check-in list</Button>
          </div>
        }
      >
        {roster.length === 0
          ? (funnelSel
              ? <EmptyState title="Nobody in this step yet" hint="This funnel step is currently empty." />
              : <EmptyState title="No RSVPs yet" hint="Responses land here as invitees reply on the investor portal." />)
          : <DataTable rows={roster} columns={cols} getRowKey={(i: any) => i.id} />}
      </SectionCard>
    </div>
  )
}

/* ============================ Agenda ============================ */

function AgendaTab({ bundle, refresh }: { bundle: EventBundle; refresh: () => Promise<void> }) {
  const getToken = useApiToken()
  const [items, setItems] = useState<any[]>(bundle.agenda)
  const [dirty, setDirty] = useState(false)
  useEffect(() => { setItems(bundle.agenda); setDirty(false) }, [bundle.agenda])

  const edit = (idx: number, patch: object) => { setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, ...patch } : it))); setDirty(true) }
  const move = (idx: number, d: number) => {
    setItems((cur) => {
      const next = [...cur]; const j = idx + d
      if (j < 0 || j >= next.length) return cur
      ;[next[idx], next[j]] = [next[j], next[idx]]
      return next
    }); setDirty(true)
  }
  const save = async () => {
    const token = await getToken()
    await api.symposium.putAgenda(token, bundle.event.id, items)
    await refresh()
  }
  const togglePublish = async () => {
    if (!confirm(bundle.event.agendaPublished
      ? 'Unpublish the agenda? Investors will see "agenda coming soon".'
      : 'Publish the agenda? Items marked visible will render on the investor portal.')) return
    const token = await getToken()
    await api.symposium.patchEvent(token, bundle.event.id, { agendaPublished: !bundle.event.agendaPublished })
    await refresh()
  }

  return (
    <SectionCard
      eyebrow="Program · what investors will see" title={<>Agenda <b>Builder</b></>}
      right={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Badge tone={bundle.event.agendaPublished ? 'ok' : 'neutral'} variant="soft" dot>
            {bundle.event.agendaPublished ? 'Published' : 'Not published'}
          </Badge>
          <Button variant="default" size="sm" onClick={() => void togglePublish()}>
            {bundle.event.agendaPublished ? 'Unpublish' : 'Publish'}
          </Button>
          <Button variant="primary" size="sm" disabled={!dirty} onClick={() => void save()}>Save agenda</Button>
        </div>
      }
    >
      <p className="dim" style={{ fontSize: 13, marginBottom: 12 }}>
        Nothing is visible on the investor portal until the agenda is published — and then only items marked visible.
      </p>
      {items.map((it, idx) => (
        <div key={idx} className="sym-agenda-row">
          <TextInput value={it.startTime ?? ''} placeholder="4:30 PM" onChange={(e) => edit(idx, { startTime: e.target.value })} style={{ width: 110 }} />
          <TextInput value={it.title} placeholder="Session title" onChange={(e) => edit(idx, { title: e.target.value })} />
          <TextInput value={it.speaker ?? ''} placeholder="Speaker" onChange={(e) => edit(idx, { speaker: e.target.value })} style={{ width: 180 }} />
          <label className="sym-agenda-vis"><input type="checkbox" checked={!!it.visibleToInvestors} onChange={(e) => edit(idx, { visibleToInvestors: e.target.checked })} />visible</label>
          <button className="sym-mini" onClick={() => move(idx, -1)}>↑</button>
          <button className="sym-mini" onClick={() => move(idx, 1)}>↓</button>
          <button className="sym-mini" onClick={() => { setItems((cur) => cur.filter((_, i) => i !== idx)); setDirty(true) }}>✕</button>
        </div>
      ))}
      <Button variant="default" size="sm" icon={<Plus size={14} />} onClick={() => { setItems((cur) => [...cur, { title: '', visibleToInvestors: true }]); setDirty(true) }}>
        Add agenda item
      </Button>
    </SectionCard>
  )
}

/* ============================ Playbook ============================ */

function PlaybookTab({ bundle, refresh }: { bundle: EventBundle; refresh: () => Promise<void> }) {
  const getToken = useApiToken()
  const pb = bundle.playbook
  const parse = (v: unknown): string[] => { try { return Array.isArray(v) ? v as string[] : JSON.parse(String(v ?? '[]')) } catch { return [] } }
  const [dos, setDos] = useState(parse(pb?.dos).join('\n'))
  const [donts, setDonts] = useState(parse(pb?.donts).join('\n'))
  const [vendors, setVendors] = useState(parse(pb?.vendors).map((v: any) => `${v.name ?? v} — ${v.role ?? ''} — ${v.notes ?? ''}`).join('\n'))
  const [gifts, setGifts] = useState(parse(pb?.giftHistory).map((g: any) => `${g.year ?? ''} — ${g.item ?? g} — ${g.sizing_notes ?? g.sizingNotes ?? ''}`).join('\n'))

  const save = async () => {
    const token = await getToken()
    const toLines = (s: string) => s.split('\n').map((l) => l.trim()).filter(Boolean)
    await api.symposium.putPlaybook(token, bundle.event.id, {
      dos: toLines(dos),
      donts: toLines(donts),
      vendors: toLines(vendors).map((l) => { const [name, role, notes] = l.split('—').map((x) => x.trim()); return { name, role, notes } }),
      giftHistory: toLines(gifts).map((l) => { const [year, item, sizing] = l.split('—').map((x) => x.trim()); return { year, item, sizing_notes: sizing } }),
    })
    await refresh()
  }

  return (
    <SectionCard
      eyebrow="Institutional memory · carried year to year" title={<>Symposium <b>Playbook</b></>}
      right={<Button variant="primary" size="sm" onClick={() => void save()}>Save playbook</Button>}
    >
      <div className="sym-grid2">
        <Field label="Do's" hint="One per line."><TextArea rows={6} value={dos} onChange={(e) => setDos(e.target.value)} /></Field>
        <Field label="Don'ts" hint="One per line."><TextArea rows={6} value={donts} onChange={(e) => setDonts(e.target.value)} /></Field>
      </div>
      <Field label="Vendors" hint="One per line: Name — Role — Notes"><TextArea rows={4} value={vendors} onChange={(e) => setVendors(e.target.value)} /></Field>
      <Field label="Gift history" hint="One per line: Year — Item — Sizing notes (2024 sun shirt M/W; 2025 socks M/W; 2026 TBD)">
        <TextArea rows={4} value={gifts} onChange={(e) => setGifts(e.target.value)} />
      </Field>
    </SectionCard>
  )
}

/* ============================ Recurring ============================ */

function RecurringTab({ bundle }: { bundle: EventBundle }) {
  return (
    <SectionCard eyebrow="Set once · repeats every year" title={<>Recurring <b>Event Setup</b></>}>
      <div className="sym-recur">
        <p><b>Cadence:</b> annual — first Thursday of November by default. Next year's event auto-drafts ~12 weeks prior; a human confirms the date and keynote.</p>
        <p><b>Carry-forward:</b> last year's invite list seeds the next event (all statuses reset to pending review, prior RSVP/attendance kept as signal); playbook, vendors, and gift history roll forward.</p>
        <p><b>Access:</b> Client Relations (WISE-IAM role <code>client_relations</code>) and CRM Admins. Ownership transferred from the Executive Assistant (Donna Waldo) to Client Relations — this console replaces the 41-tab spreadsheet.</p>
        <p className="dim" style={{ fontSize: 13 }}>
          Annual rollover (clone into {Number(bundle.event.year) + 1}) ships with a later slice — it will link back via created-from for year-over-year comparison.
        </p>
      </div>
    </SectionCard>
  )
}
